/* ═══════════════════════════════════════════════════════════════
   Team — Manage team members, named teams, and project assignments
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeTypes,
  type NodeProps,
  type OnNodeDrag,
  type NodeMouseHandler,
  type Connection,
  type IsValidConnection,
  type EdgeProps,
  type EdgeTypes,
  ConnectionMode,
  ReactFlowProvider,
  useReactFlow,
  useViewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { SearchableSelect } from '../components/SearchableSelect';
import EmptyState from '../components/EmptyState';
import { TeamService } from '../services/teamService';
import { listCached } from '../services/cloudCache';
import { ProjectService } from '../services/projectService';
import { AdminService } from '../services/adminService';
import { InvitationService } from '../services/invitationService';
import type {
  TeamMember,
  Team,
  TeamAssignment,
  ProjectAssignment,
  Project,
  Role,
  BillingRole,
  Invitation,
} from '../../shared/types';
import type { Department, OrgChartEdge, OrgChartJunction } from '../../shared/entities';
import { OrgChartEdgeService } from '../services/orgChartEdgeService';
import { OrgChartJunctionService } from '../services/orgChartJunctionService';
import {
  sendOrgChartDrag,
  sendOrgChartCursor,
  sendOrgChartCursorLeave,
  onOrgChartDrag,
  onOrgChartCursor,
} from '../services/liveSync';
import { DepartmentService } from '../services/departmentService';
import type { AuthUser } from '../services/authService';
import { hasPermission } from '../../shared/permissions';
import { TEAM_COLORS, ORG_EDGE_COLORS } from '../lib/palette';

interface TeamPageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
  /** One-shot drill from another page: open the member editor for memberId. */
  deepLink?: { view: 'team'; memberId?: string } | null;
  onDeepLinkConsumed?: () => void;
}

type ActiveTab = 'members' | 'teams' | 'orgchart';

// ─── Empty factories ───

function emptyMember(): TeamMember {
  return {
    id: '',
    name: '',
    email: '',
    role: '',
    roleId: undefined,
    reportsTo: undefined,
    hourlyRate: undefined,
    active: true,
    createdAt: '',
    updatedAt: '',
  };
}

function emptyTeam(): Team {
  return {
    id: '',
    name: '',
    description: '',
    color: TEAM_COLORS[Math.floor(Math.random() * TEAM_COLORS.length)],
    createdAt: '',
    updatedAt: '',
  };
}

// ─── Main Page ───

function TeamPage({ toast, authUser, deepLink, onDeepLinkConsumed }: TeamPageProps) {
  const [tab, setTab] = useState<ActiveTab>('members');
  // Pending deep-link memberId to open once members are loaded.
  const [pendingOpenMemberId, setPendingOpenMemberId] = useState<string | null>(null);

  // Data — initialize from cache so pages never flash empty on remount
  const [members, setMembers] = useState<TeamMember[]>(() =>
    listCached<TeamMember>('team_members')
  );
  const [roles, setRoles] = useState<Role[]>(() => listCached<Role>('roles'));
  const [projects, setProjects] = useState<Project[]>(() => listCached<Project>('projects'));
  const [allAssignments, setAllAssignments] = useState<ProjectAssignment[]>(() =>
    listCached<ProjectAssignment>('project_assignments')
  );
  const [teams, setTeams] = useState<Team[]>(() => listCached<Team>('teams'));
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>(() =>
    listCached<TeamAssignment>('team_assignments')
  );
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>(() =>
    listCached<BillingRole>('billing_roles')
  );
  const [departments, setDepartments] = useState<Department[]>(() =>
    listCached<Department>('departments')
  );
  const [orgChartEdges, setOrgChartEdges] = useState<OrgChartEdge[]>(() =>
    listCached<OrgChartEdge>('org_chart_edges')
  );
  const [orgChartJunctions, setOrgChartJunctions] = useState<OrgChartJunction[]>(() =>
    listCached<OrgChartJunction>('org_chart_junctions')
  );
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  // Invite member state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviteBillingRoleId, setInviteBillingRoleId] = useState('');

  // Member editor state
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [memberDirty, setMemberDirty] = useState(false);
  const [memberProjectAssignments, setMemberProjectAssignments] = useState<ProjectAssignment[]>([]);
  const [memberTeamAssignments, setMemberTeamAssignments] = useState<TeamAssignment[]>([]);
  const [addingProject, setAddingProject] = useState(false);

  // Team editor state
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamDirty, setTeamDirty] = useState(false);
  const [editingTeamAssignments, setEditingTeamAssignments] = useState<TeamAssignment[]>([]);
  const [addingTeamMember, setAddingTeamMember] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadAll(retry = true) {
    try {
      const [m, r, p, a, t, ta, br, inv, depts, oce, ocj] = await Promise.allSettled([
        TeamService.list(),
        AdminService.listRoles(),
        ProjectService.list(),
        TeamService.listAssignments(),
        TeamService.listTeams(),
        TeamService.listTeamAssignments(),
        AdminService.billingRoles.list().catch(() => [] as any[]),
        InvitationService.list().catch(() => [] as Invitation[]),
        DepartmentService.list().catch(() => [] as Department[]),
        OrgChartEdgeService.list().catch(() => [] as OrgChartEdge[]),
        OrgChartJunctionService.list().catch(() => [] as OrgChartJunction[]),
      ]);
      console.log(
        '[team] loadAll got',
        m.status === 'fulfilled' ? m.value.length : '?',
        'members,',
        t.status === 'fulfilled' ? t.value.length : '?',
        'teams,',
        p.status === 'fulfilled' ? p.value.length : '?',
        'projects'
      );
      // Spread into fresh arrays so React sees new references. The
      // cache returns the SAME underlying rows array each time and
      // only mutates items in place; handing that back to setState
      // means React bails on its identity check and the chart doesn't
      // re-render — the "leave and come back to see changes" bug.
      // Each slice is only applied if its fetch actually succeeded —
      // a single failed request no longer blanks out every other
      // already-loaded section of the page (Promise.allSettled).
      if (m.status === 'fulfilled') setMembers([...m.value]);
      else console.error('TeamPage loadAll: members failed:', m.reason);
      if (r.status === 'fulfilled') setRoles([...r.value]);
      else console.error('TeamPage loadAll: roles failed:', r.reason);
      if (p.status === 'fulfilled') setProjects([...p.value]);
      else console.error('TeamPage loadAll: projects failed:', p.reason);
      if (a.status === 'fulfilled') setAllAssignments([...a.value]);
      else console.error('TeamPage loadAll: assignments failed:', a.reason);
      if (t.status === 'fulfilled') setTeams([...t.value]);
      else console.error('TeamPage loadAll: teams failed:', t.reason);
      if (ta.status === 'fulfilled') setTeamAssignments([...ta.value]);
      else console.error('TeamPage loadAll: team assignments failed:', ta.reason);
      if (br.status === 'fulfilled') setBillingRoles([...br.value]);
      if (depts.status === 'fulfilled') setDepartments([...depts.value]);
      if (oce.status === 'fulfilled') setOrgChartEdges([...oce.value]);
      if (ocj.status === 'fulfilled') setOrgChartJunctions([...ocj.value]);
      if (inv.status === 'fulfilled')
        setInvitations(inv.value.filter((i) => i.status === 'pending'));
      const anyFailed = [m, r, p, a, t, ta].some((res) => res.status === 'rejected');
      if (anyFailed && retry) setTimeout(() => loadAll(false), 1500);
    } catch (e) {
      console.error('TeamPage loadAll failed:', e);
      if (retry) setTimeout(() => loadAll(false), 1500);
    }
  }

  // ─── Helpers ───

  // Resolve a Role row from a member, preferring the FK and falling back
  // to a name lookup against the legacy `role` text column. Legacy data
  // (members created before role_id was wired) ships only `role='user'`,
  // which is why the editor's roleId-bound dropdown showed unselected
  // even though the table column read "User".
  const resolveRole = useCallback(
    (m: TeamMember) => {
      if (m.roleId) {
        const r = roles.find((rr) => rr.id === m.roleId);
        if (r) return r;
      }
      if (m.role) {
        const lowered = m.role.toLowerCase();
        return roles.find((rr) => rr.name?.toLowerCase() === lowered);
      }
      return undefined;
    },
    [roles]
  );

  const roleName = useCallback(
    (m: TeamMember): string => {
      const r = resolveRole(m);
      if (r) return r.name;
      return m.role || '';
    },
    [resolveRole]
  );

  /** Stamp a synthetic roleId on a member object when it's missing but the
   *  legacy `role` text matches a known Role. Used when seeding the editor
   *  state so the dropdown shows the right selection on open; saving the
   *  member then persists the linked roleId, healing the row for next time. */
  const linkRoleId = useCallback(
    (m: TeamMember): TeamMember => {
      if (m.roleId) return m;
      const r = resolveRole(m);
      return r ? { ...m, roleId: r.id } : m;
    },
    [roles]
  );

  // Consume incoming deep-link once: switch to members tab and queue the
  // memberId to open as soon as `members` is loaded (handled below).
  useEffect(() => {
    if (!deepLink) return;
    setTab('members');
    if (deepLink.memberId) setPendingOpenMemberId(deepLink.memberId);
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  // Once members have loaded, open the requested editor (if any).
  useEffect(() => {
    if (!pendingOpenMemberId || members.length === 0) return;
    const target = members.find((m) => m.id === pendingOpenMemberId);
    if (target) setEditingMember(linkRoleId({ ...target }));
    setPendingOpenMemberId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenMemberId, members]);

  const memberName = useCallback(
    (id?: string): string => {
      if (!id) return '';
      return members.find((m) => m.id === id)?.name || '';
    },
    [members]
  );

  const teamName = useCallback(
    (id: string): string => {
      return teams.find((t) => t.id === id)?.name || '';
    },
    [teams]
  );

  function getMemberTeamAssignments(memberId: string): TeamAssignment[] {
    return teamAssignments.filter((ta) => ta.teamMemberId === memberId);
  }

  function getTeamMemberAssignments(teamId: string): TeamAssignment[] {
    return teamAssignments.filter((ta) => ta.teamId === teamId);
  }

  function getTeamLeader(teamId: string): string {
    const leader = teamAssignments.find((ta) => ta.teamId === teamId && ta.teamRole === 'leader');
    return leader ? memberName(leader.teamMemberId) : '';
  }

  function getProjectAssignmentCount(memberId: string): number {
    return allAssignments.filter((a) => a.teamMemberId === memberId).length;
  }

  function projectById(id: string): Project | undefined {
    return projects.find((p) => p.id === id);
  }

  // ─── Member table columns ───

  const memberColumns: Column<TeamMember>[] = [
    {
      key: 'name',
      label: 'Name',
      width: 180,
      render: (m) => <span style={{ fontWeight: 600 }}>{m.name}</span>,
      getValue: (m) => m.name,
      editable: true,
      editType: 'text',
    },
    {
      key: 'email',
      label: 'Email',
      width: 200,
      render: (m) => (
        <span style={{ color: m.email ? 'var(--tx)' : 'var(--tm)', fontSize: 13 }}>
          {m.email || '\u2013'}
        </span>
      ),
      getValue: (m) => m.email || '',
      editable: true,
      editType: 'text',
    },
    {
      key: 'roleId',
      label: 'Privilege Role',
      width: 140,
      render: (m) => {
        const rn = roleName(m);
        return rn ? (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 6,
              background: 'var(--sa)',
              color: 'var(--tx)',
              fontSize: 11,
              fontWeight: 600,
              border: '1px solid var(--bd)',
              whiteSpace: 'nowrap',
            }}
          >
            {rn}
          </span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>&ndash;</span>
        );
      },
      getValue: (m) => roleName(m),
      editable: true,
      editType: 'select',
      editOptions: [
        { value: '', label: '-- None --' },
        ...roles.map((r) => ({ value: r.id, label: r.name })),
      ],
    },
    {
      key: 'billingRoleId',
      label: 'Title',
      width: 130,
      render: (m) => {
        const br = m.billingRoleId ? billingRoles.find((r) => r.id === m.billingRoleId) : null;
        return br ? (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 6,
              background: 'rgba(var(--acr),0.08)',
              color: 'var(--ac)',
              fontSize: 11,
              fontWeight: 600,
              border: '1px solid rgba(var(--acr),0.2)',
              whiteSpace: 'nowrap',
            }}
          >
            {br.name}
          </span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>&ndash;</span>
        );
      },
      getValue: (m) => {
        const br = m.billingRoleId ? billingRoles.find((r) => r.id === m.billingRoleId) : null;
        return br?.name || '';
      },
      editable: true,
      editType: 'select',
      editOptions: [
        { value: '', label: '-- None --' },
        ...billingRoles.map((r) => ({ value: r.id, label: r.name })),
      ],
    },
    {
      key: 'teams',
      label: 'Teams',
      width: 200,
      sortable: false,
      render: (m) => {
        const mts = getMemberTeamAssignments(m.id);
        if (mts.length === 0) return <span style={{ color: 'var(--tm)' }}>&ndash;</span>;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {mts.map((ta) => {
              const team = teams.find((t) => t.id === ta.teamId);
              const tc = team?.color;
              return (
                <span
                  key={ta.id}
                  style={{
                    padding: '1px 8px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    background: tc
                      ? tc + '18'
                      : ta.teamRole === 'leader'
                        ? 'rgba(var(--acr),0.15)'
                        : 'var(--sa)',
                    color: tc || (ta.teamRole === 'leader' ? 'var(--ac)' : 'var(--tm)'),
                    border: `1px solid ${tc ? tc + '40' : ta.teamRole === 'leader' ? 'rgba(var(--acr),0.3)' : 'var(--bd)'}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {teamName(ta.teamId)}
                  {ta.teamRole === 'leader' && (
                    <span style={{ marginLeft: 3, fontSize: 11 }}>&#9733;</span>
                  )}
                </span>
              );
            })}
          </div>
        );
      },
      getValue: (m) =>
        getMemberTeamAssignments(m.id)
          .map((ta) => teamName(ta.teamId))
          .join(', '),
    },
    {
      key: 'projects',
      label: 'Projects',
      width: 80,
      render: (m) => {
        const count = getProjectAssignmentCount(m.id);
        return count > 0 ? (
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ac)' }}>{count}</span>
        ) : (
          <span style={{ color: 'var(--tm)' }}>&ndash;</span>
        );
      },
      getValue: (m) => getProjectAssignmentCount(m.id),
    },
    {
      key: 'active',
      label: 'Status',
      width: 90,
      render: (m) =>
        m.active ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '2px 9px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ok)',
              background: 'color-mix(in srgb, var(--ok) 14%, transparent)',
              border: '1px solid color-mix(in srgb, var(--ok) 26%, transparent)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--ok)',
                flexShrink: 0,
              }}
            />
            Active
          </span>
        ) : (
          <span
            style={{
              padding: '2px 9px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--tm)',
              background: 'var(--sa)',
              border: '1px solid var(--bd)',
            }}
          >
            Inactive
          </span>
        ),
      getValue: (m) => (m.active ? 'Active' : 'Inactive'),
      editable: true,
      editType: 'checkbox',
    },
  ];

  const memberGroupOptions: GroupOption<TeamMember>[] = [
    { key: 'role', label: 'Role', getValue: (m) => roleName(m) || 'No Role' },
    {
      key: 'team',
      label: 'Team',
      getValue: (m) => {
        const mts = getMemberTeamAssignments(m.id);
        return mts.length > 0 ? teamName(mts[0].teamId) : 'No Team';
      },
    },
    { key: 'status', label: 'Status', getValue: (m) => (m.active ? 'Active' : 'Inactive') },
  ];

  // ─── Permission gating ───
  const canViewRoles =
    hasPermission(authUser?.permissions, 'admin_center.view') ||
    hasPermission(authUser?.permissions, 'admin_center.manage_roles');
  const canViewBilling = hasPermission(authUser?.permissions, 'admin_center.manage_billing');
  const canEditMembers = hasPermission(authUser?.permissions, 'team_members.edit');
  const canCreateMembers = hasPermission(authUser?.permissions, 'team_members.create');
  const canDeactivateMembers = hasPermission(authUser?.permissions, 'team_members.deactivate');
  const visibleMemberColumns = useMemo(
    () =>
      memberColumns.filter((col) => {
        if (col.key === 'roleId' && !canViewRoles) return false;
        if (col.key === 'billingRoleId' && !canViewBilling) return false;
        return true;
      }),
    [memberColumns, canViewRoles, canViewBilling]
  );

  // ─── Member editor actions ───

  async function openMemberEditor(m: TeamMember) {
    // Heal legacy data on open: members created before role_id was wired
    // ship `role='user'` text without a matching roleId FK. linkRoleId
    // resolves the FK from the text so the Privilege dropdown shows the
    // correct selection and saving stamps the FK back.
    setEditingMember(linkRoleId({ ...m }));
    setMemberDirty(false);
    setAddingProject(false);
    if (m.id) {
      const [pa, mta] = await Promise.allSettled([
        TeamService.listAssignments(m.id),
        TeamService.listTeamAssignments(undefined, m.id),
      ]);
      if (pa.status === 'fulfilled') setMemberProjectAssignments(pa.value);
      else console.error('openMemberEditor: project assignments failed:', pa.reason);
      if (mta.status === 'fulfilled') setMemberTeamAssignments(mta.value);
      else console.error('openMemberEditor: team assignments failed:', mta.reason);
    } else {
      setMemberProjectAssignments([]);
      setMemberTeamAssignments([]);
    }
  }

  function closeMemberEditor(force = false) {
    if (!force && memberDirty && !confirm('Discard unsaved changes?')) return;
    setEditingMember(null);
    setMemberDirty(false);
    setMemberProjectAssignments([]);
    setMemberTeamAssignments([]);
    setAddingProject(false);
  }

  function updateMember(updates: Partial<TeamMember>) {
    setEditingMember((prev) => (prev ? { ...prev, ...updates } : prev));
    setMemberDirty(true);
  }

  async function saveMember() {
    if (!editingMember) return;
    if (!editingMember.name.trim()) {
      toast('Name is required', true);
      return;
    }
    if (editingMember.roleId) {
      const r = roles.find((r) => r.id === editingMember.roleId);
      if (r) editingMember.role = r.name;
    }
    await TeamService.save(editingMember);
    await loadAll();
    setEditingMember(null);
    setMemberDirty(false);
    toast('Team member saved');
  }

  async function changeReportsTo(memberId: string, newManagerId: string | undefined) {
    const member = members.find((m) => m.id === memberId);
    if (!member) return;
    await TeamService.save({ ...member, reportsTo: newManagerId || '' });
    await loadAll();
    if (newManagerId && newManagerId !== '__root__') {
      toast(`${member.name} now reports to ${members.find((m) => m.id === newManagerId)?.name}`);
    } else {
      toast(`${member.name} moved to top-level`);
    }
  }

  async function handleTeamAssign(memberId: string, teamId: string) {
    // Enforce single-team: remove any existing team assignments first
    const existing = teamAssignments.filter((a) => a.teamMemberId === memberId);
    for (const ta of existing) {
      if (ta.teamId !== teamId) await TeamService.removeFromTeam(ta.id);
    }
    // Only add if not already in this team
    if (!existing.some((a) => a.teamId === teamId)) {
      await TeamService.assignToTeam(teamId, memberId, 'member');
    }
    await loadAll();
    const m = members.find((m) => m.id === memberId);
    const t = teams.find((t) => t.id === teamId);
    toast(`${m?.name || 'Member'} added to ${t?.name || 'team'}`);
  }

  async function handleTeamRemove(memberId: string, teamId: string) {
    const ta = teamAssignments.find((a) => a.teamMemberId === memberId && a.teamId === teamId);
    if (!ta) return;
    await TeamService.removeFromTeam(ta.id);
    await loadAll();
    const m = members.find((m) => m.id === memberId);
    const t = teams.find((t) => t.id === teamId);
    toast(`${m?.name || 'Member'} removed from ${t?.name || 'team'}`);
  }

  async function handleToggleTeamRole(memberId: string, teamId: string) {
    const ta = teamAssignments.find((a) => a.teamMemberId === memberId && a.teamId === teamId);
    if (!ta) return;
    const newRole = ta.teamRole === 'leader' ? 'member' : 'leader';
    await TeamService.updateTeamAssignment({ ...ta, teamRole: newRole });
    await loadAll();
    const m = members.find((m) => m.id === memberId);
    toast(`${m?.name || 'Member'} is now ${newRole}`);
  }

  async function handleInsertBetween(memberId: string, parentId: string, childId: string) {
    const member = members.find((m) => m.id === memberId);
    const parent = members.find((m) => m.id === parentId);
    if (!member) return;
    await TeamService.save({ ...member, reportsTo: parentId });
    const refreshed = await TeamService.list();
    const child = refreshed.find((m) => m.id === childId);
    if (child) await TeamService.save({ ...child, reportsTo: memberId });
    await loadAll();
    toast(`${member.name} inserted below ${parent?.name || 'top'}`);
  }

  async function handleMakeParentOf(memberId: string, childNodeId: string) {
    const member = members.find((m) => m.id === memberId);
    const child = members.find((m) => m.id === childNodeId);
    if (!member || !child) return;
    await TeamService.save({ ...member, reportsTo: child.reportsTo || '' });
    const refreshed = await TeamService.list();
    const updatedChild = refreshed.find((m) => m.id === childNodeId);
    if (updatedChild) await TeamService.save({ ...updatedChild, reportsTo: memberId });
    await loadAll();
    toast(`${child.name} now reports to ${member.name}`);
  }

  async function deleteMember(id: string) {
    if (!confirm('Delete this team member? This cannot be undone.')) return;
    await TeamService.delete(id);
    if (editingMember?.id === id) {
      setEditingMember(null);
      setMemberDirty(false);
    }
    await loadAll();
    toast('Team member deleted');
  }

  async function removeFromChart(memberId: string) {
    const member = members.find((m) => m.id === memberId);
    if (!member) return;
    // Clear hierarchy (including __root__ sentinel)
    if (member.reportsTo) await TeamService.save({ ...member, reportsTo: '' });
    // Remove all team assignments
    const tas = teamAssignments.filter((a) => a.teamMemberId === memberId);
    for (const ta of tas) await TeamService.removeFromTeam(ta.id);
    await loadAll();
    toast(`${member.name} removed from chart`);
  }

  /** Create the member row immediately — no invitation, no email. They can be
   *  invited (or sign up with the firm code) later; signup links by email. */
  async function addMemberDirect() {
    if (!inviteEmail.trim()) {
      toast('Email is required', true);
      return;
    }
    if (!inviteRoleId) {
      toast('Role is required', true);
      return;
    }
    const existing = members.find((m) => m.email?.toLowerCase() === inviteEmail.toLowerCase());
    if (existing) {
      toast(`${existing.name} is already a member`, true);
      return;
    }
    const role = roles.find((r) => r.id === inviteRoleId);
    const name = inviteName.trim() || inviteEmail.trim().split('@')[0];
    await TeamService.save({
      id: '',
      name,
      email: inviteEmail.trim().toLowerCase(),
      role: role?.name || 'User',
      roleId: inviteRoleId,
      billingRoleId: inviteBillingRoleId || undefined,
      active: true,
      employmentType: 'salary',
      createdAt: '',
      updatedAt: '',
    } as TeamMember);
    setShowInviteForm(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRoleId('');
    setInviteBillingRoleId('');
    await loadAll();
    toast(`${name} added to the team`);
  }

  async function inviteMember() {
    if (!inviteEmail.trim()) {
      toast('Email is required', true);
      return;
    }
    if (!inviteRoleId) {
      toast('Role is required', true);
      return;
    }
    const existing = members.find((m) => m.email?.toLowerCase() === inviteEmail.toLowerCase());
    if (existing) {
      toast(`${existing.name} is already a member`, true);
      return;
    }
    const existingInv = invitations.find(
      (i) => i.email.toLowerCase() === inviteEmail.toLowerCase()
    );
    if (existingInv) {
      toast('An invitation is already pending for this email', true);
      return;
    }
    const saved = await InvitationService.save({
      id: '',
      email: inviteEmail.trim().toLowerCase(),
      name: inviteName.trim() || undefined,
      roleId: inviteRoleId || undefined,
      billingRoleId: inviteBillingRoleId || undefined,
      invitedBy: members.find((m) => m.active)?.id || '',
      status: 'pending',
      createdAt: new Date().toISOString(),
    });
    setShowInviteForm(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRoleId('');
    setInviteBillingRoleId('');
    await loadAll();

    // Fire-and-forget email send. If SMTP isn't configured on the server the
    // invite row still exists — admin can resend manually later or paste the
    // code from the invitations table.
    try {
      await InvitationService.sendEmail((saved as any).id);
      toast(`Invitation email sent to ${inviteEmail.trim()}`);
    } catch (err: any) {
      toast(`Invitation saved but email failed: ${err.message || err}`, true);
    }
  }

  async function cancelInvitation(id: string) {
    await InvitationService.cancel(id);
    await loadAll();
    toast('Invitation cancelled');
  }

  async function assignProject(projectId: string) {
    if (!editingMember?.id) return;
    await TeamService.assign(projectId, editingMember.id);
    await loadAll();
    const pa = await TeamService.listAssignments(editingMember.id);
    setMemberProjectAssignments(pa);
    setAddingProject(false);
    toast('Project assigned');
  }

  async function unassignProject(assignmentId: string) {
    if (!editingMember?.id) return;
    await TeamService.unassign(assignmentId);
    await loadAll();
    const pa = await TeamService.listAssignments(editingMember.id);
    setMemberProjectAssignments(pa);
    toast('Project removed');
  }

  // ─── Team editor actions ───

  async function openTeamEditor(t: Team) {
    setEditingTeam({ ...t });
    setTeamDirty(false);
    setAddingTeamMember(false);
    // Use already-loaded teamAssignments state instead of re-querying
    setEditingTeamAssignments(t.id ? teamAssignments.filter((a) => a.teamId === t.id) : []);
  }

  function closeTeamEditor(force = false) {
    if (!force && teamDirty && !confirm('Discard unsaved changes?')) return;
    setEditingTeam(null);
    setTeamDirty(false);
    setEditingTeamAssignments([]);
    setAddingTeamMember(false);
  }

  function updateTeam(updates: Partial<Team>) {
    setEditingTeam((prev) => (prev ? { ...prev, ...updates } : prev));
    setTeamDirty(true);
  }

  async function saveTeam() {
    if (!editingTeam) return;
    if (!editingTeam.name.trim()) {
      toast('Team name is required', true);
      return;
    }
    await TeamService.saveTeam(editingTeam);
    await loadAll();
    setEditingTeam(null);
    setTeamDirty(false);
    toast('Team saved');
  }

  async function deleteTeam(id: string) {
    if (!confirm('Delete this team? Members will not be deleted.')) return;
    await TeamService.deleteTeam(id);
    if (editingTeam?.id === id) {
      setEditingTeam(null);
      setTeamDirty(false);
    }
    await loadAll();
    toast('Team deleted');
  }

  async function addMemberToTeam(memberId: string, role: 'leader' | 'member') {
    if (!editingTeam?.id) return;
    // Enforce single-team: remove from any other team first
    const existing = teamAssignments.filter(
      (a) => a.teamMemberId === memberId && a.teamId !== editingTeam.id
    );
    for (const ta of existing) await TeamService.removeFromTeam(ta.id);
    await TeamService.assignToTeam(editingTeam.id, memberId, role);
    await loadAll();
    const ta = await TeamService.listTeamAssignments(editingTeam.id);
    setEditingTeamAssignments(ta);
    // Keep picker open so user can add multiple members without re-opening
    toast('Member added to team');
  }

  async function removeMemberFromTeam(assignmentId: string) {
    if (!editingTeam?.id) return;
    await TeamService.removeFromTeam(assignmentId);
    await loadAll();
    const ta = await TeamService.listTeamAssignments(editingTeam.id);
    setEditingTeamAssignments(ta);
    toast('Member removed from team');
  }

  async function changeTeamMemberRole(assignmentId: string, newRole: 'leader' | 'member') {
    if (!editingTeam?.id) return;
    const ta = editingTeamAssignments.find((a) => a.id === assignmentId);
    if (!ta) return;
    await TeamService.updateTeamAssignment({ ...ta, teamRole: newRole });
    await loadAll();
    const updated = await TeamService.listTeamAssignments(editingTeam.id);
    setEditingTeamAssignments(updated);
  }

  // ─── Computed ───

  const activeCount = members.filter((m) => m.active).length;

  const assignedMemberIds = new Set(editingTeamAssignments.map((ta) => ta.teamMemberId));
  const availableTeamMembers = members.filter((m) => !assignedMemberIds.has(m.id));

  const assignedProjectIds = new Set(memberProjectAssignments.map((a) => a.projectId));
  const availableProjects = projects
    .filter((p) => !assignedProjectIds.has(p.id))
    .sort((a, b) => (a.projectNumber || '').localeCompare(b.projectNumber || ''));

  // ─── Render ───

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Page header */}
      <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
          }}
        >
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
              Team
            </h2>
            <p style={{ fontSize: 13, color: 'var(--tm)', margin: '4px 0 0' }}>
              {activeCount} active member{activeCount !== 1 ? 's' : ''} &middot; {teams.length} team
              {teams.length !== 1 ? 's' : ''}
              {authUser?.firmSlug && canCreateMembers && (
                <>
                  {' '}
                  &middot; Company Code{' '}
                  <span
                    style={{
                      cursor: 'pointer',
                      userSelect: 'all',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '1px 7px',
                      borderRadius: 6,
                      background: 'color-mix(in srgb, var(--ac) 10%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--ac) 22%, transparent)',
                      color: 'var(--ac)',
                      fontWeight: 600,
                      fontSize: 13,
                      verticalAlign: 'baseline',
                    }}
                    title="Click to copy"
                    onClick={() => {
                      navigator.clipboard.writeText(authUser.firmSlug);
                      toast('Company code copied!');
                    }}
                  >
                    {authUser.firmSlug}
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </span>
                </>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEditMembers && (
              <button
                onClick={() => {
                  setTab('teams');
                  openTeamEditor(emptyTeam());
                }}
                style={{ ...btnSecondary, ...btnWithIcon }}
              >
                <PlusIcon />
                New Team
              </button>
            )}
            {canCreateMembers && (
              <button
                onClick={() => {
                  setTab('members');
                  {
                    const ur = roles.find((r) => r.name?.toLowerCase() === 'user');
                    if (ur && !inviteRoleId) setInviteRoleId(ur.id);
                    setShowInviteForm(true);
                  }
                }}
                style={{ ...btnPrimary, ...btnWithIcon }}
              >
                <PlusIcon />
                Add Member
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--bd)' }}>
          {[
            { id: 'members' as ActiveTab, label: `Members (${members.length})` },
            { id: 'teams' as ActiveTab, label: `Teams (${teams.length})` },
            { id: 'orgchart' as ActiveTab, label: 'Org Chart' },
          ].map((t) => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  background: 'transparent',
                  color: isActive ? 'var(--ac)' : 'var(--tm)',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  borderBottom: isActive ? '2px solid var(--ac)' : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'color .15s, background .15s',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'var(--sa)';
                    e.currentTarget.style.color = 'var(--tx)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--tm)';
                  }
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: '16px 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {tab === 'members' && (
          <>
            {/* Pending Invitations */}
            {invitations.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--tm)',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  Pending Invitations ({invitations.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {invitations.map((inv) => (
                    <div
                      key={inv.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '8px 14px',
                        background: 'var(--sf)',
                        border: '1px dashed var(--bd)',
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'var(--sa)',
                          border: '1px solid var(--bd)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--tm)"
                          strokeWidth="2"
                        >
                          <path d="M4 4h16v16H4z" />
                          <path d="M22 6l-10 7L2 6" />
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.name || inv.email}</div>
                        {inv.name && (
                          <div style={{ fontSize: 11, color: 'var(--tm)' }}>{inv.email}</div>
                        )}
                      </div>
                      {inv.roleId &&
                        (() => {
                          const r = roles.find((r) => r.id === inv.roleId);
                          return r ? (
                            <span
                              style={{
                                fontSize: 11,
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: 'var(--sa)',
                                border: '1px solid var(--bd)',
                                fontWeight: 500,
                              }}
                            >
                              {r.name}
                            </span>
                          ) : null;
                        })()}
                      <span style={{ fontSize: 11, color: 'var(--tm)', fontStyle: 'italic' }}>
                        Pending
                      </span>
                      <button
                        onClick={() => cancelInvitation(inv.id)}
                        style={{ ...iconBtn, color: 'var(--dg)' }}
                        title="Cancel invitation"
                        aria-label="Cancel invitation"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invite form (inline) */}
            {showInviteForm && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 16,
                  background: 'var(--sf)',
                  border: '1px solid var(--bd)',
                  borderRadius: 10,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                  Add a New Team Member
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--tm)',
                    }}
                  >
                    Email *
                    <input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="name@company.com"
                      autoFocus
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: 'var(--ib)',
                        color: 'var(--tx)',
                        fontSize: 13,
                        width: 220,
                      }}
                    />
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--tm)',
                    }}
                  >
                    Name
                    <input
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="Full name (optional)"
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: 'var(--ib)',
                        color: 'var(--tx)',
                        fontSize: 13,
                        width: 180,
                      }}
                    />
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--tm)',
                    }}
                  >
                    Role
                    <div style={{ width: 180 }}>
                      <SearchableSelect
                        value={inviteRoleId}
                        options={roles.map((r) => ({ value: r.id, label: r.name }))}
                        onChange={(v) => setInviteRoleId(v)}
                        placeholder="-- Select role --"
                        searchPlaceholder="Search roles…"
                        clearable={false}
                      />
                    </div>
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--tm)',
                    }}
                  >
                    Title
                    <div style={{ width: 180 }}>
                      <SearchableSelect
                        value={inviteBillingRoleId}
                        options={billingRoles.map((br) => ({ value: br.id, label: br.name }))}
                        onChange={(v) => setInviteBillingRoleId(v)}
                        placeholder="-- None --"
                        searchPlaceholder="Search titles…"
                      />
                    </div>
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={addMemberDirect} style={btnPrimary}>
                      Add Member
                    </button>
                    <button onClick={inviteMember} style={btnSecondary}>
                      Add &amp; Send Invite
                    </button>
                    <button
                      onClick={() => {
                        setShowInviteForm(false);
                        setInviteEmail('');
                        setInviteName('');
                      }}
                      style={btnSecondary}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <p style={{ fontSize: 11, color: 'var(--tm)', margin: '10px 0 0' }}>
                  <strong>Add Member</strong> creates the person immediately — no email sent; they
                  can sign up later with your firm code and their account links by email.
                  <strong> Add &amp; Send Invite</strong> also emails them a signup invitation
                  pre-configured with the role and title you set here.
                </p>
              </div>
            )}

            {/* Active Members table */}
            <DataTable<TeamMember>
              columns={visibleMemberColumns}
              data={members}
              keyField="id"
              storageKey="team-members"
              onRowClick={canEditMembers ? openMemberEditor : undefined}
              groupOptions={memberGroupOptions}
              actions={
                canDeactivateMembers
                  ? (m) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMember(m.id);
                        }}
                        style={iconBtn}
                        title="Deactivate member"
                        aria-label="Deactivate member"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>
                    )
                  : undefined
              }
              emptyIcon={'○'}
              emptyTitle="No Team Members Yet"
              emptyMessage="Invite your first team member to get started."
              initialSort={{ key: 'name', direction: 'asc' }}
            />
          </>
        )}

        {tab === 'teams' && (
          <TeamsTab
            teams={teams}
            members={members}
            teamAssignments={teamAssignments}
            memberName={memberName}
            getTeamMemberAssignments={getTeamMemberAssignments}
            getTeamLeader={getTeamLeader}
            onEdit={openTeamEditor}
            onDelete={deleteTeam}
            onNew={() => openTeamEditor(emptyTeam())}
          />
        )}

        {tab === 'orgchart' && (
          <OrgChart
            members={members}
            teams={teams}
            teamAssignments={teamAssignments}
            departments={departments}
            roles={roles}
            billingRoles={billingRoles}
            roleName={roleName}
            onMemberClick={openMemberEditor}
            onReportsToChange={changeReportsTo}
            onTeamAssign={handleTeamAssign}
            onTeamRemove={handleTeamRemove}
            onToggleTeamRole={handleToggleTeamRole}
            onInsertBetween={handleInsertBetween}
            onMakeParentOf={handleMakeParentOf}
            onRemoveFromChart={removeFromChart}
            onDeleteMember={deleteMember}
            onAddToChart={async (memberId, x, y) => {
              const m = members.find((x) => x.id === memberId);
              if (!m) return;
              await TeamService.save({ ...m, reportsTo: '__root__', orgX: x, orgY: y });
              await loadAll();
            }}
            onSavePositions={async (updates) => {
              for (const u of updates) {
                const m = members.find((x) => x.id === u.id);
                if (m) await TeamService.save({ ...m, orgX: u.orgX, orgY: u.orgY });
              }
            }}
            orgChartEdges={orgChartEdges}
            orgChartJunctions={orgChartJunctions}
            onCreateJunction={async (x, y) => {
              const now = new Date().toISOString();
              const junction: OrgChartJunction = {
                id: crypto.randomUUID(),
                x,
                y,
                createdAt: now,
                updatedAt: now,
              };
              await OrgChartJunctionService.save(junction);
              await loadAll();
              return junction;
            }}
            onUpdateJunction={async (id, patch) => {
              const existing = orgChartJunctions.find((j) => j.id === id);
              if (!existing) return;
              await OrgChartJunctionService.save({ ...existing, ...patch });
              await loadAll();
            }}
            onDeleteJunction={async (id) => {
              // Cascade: drop every edge referencing this junction
              // before removing the junction itself so the chart
              // doesn't briefly render orphaned dangling edges.
              const orphaned = orgChartEdges.filter(
                (e) =>
                  (e.sourceType === 'junction' && e.sourceMemberId === id) ||
                  (e.targetType === 'junction' && e.targetMemberId === id)
              );
              for (const e of orphaned) await OrgChartEdgeService.delete(e.id);
              await OrgChartJunctionService.delete(id);
              await loadAll();
            }}
            onCreateEdge={async (patch) => {
              if (!patch.sourceMemberId || !patch.targetMemberId) return;
              const sourceType = patch.sourceType || 'member';
              const targetType = patch.targetType || 'member';
              const now = new Date().toISOString();
              const edge: OrgChartEdge = {
                id: patch.id || crypto.randomUUID(),
                sourceMemberId: patch.sourceMemberId,
                targetMemberId: patch.targetMemberId,
                sourceType,
                targetType,
                style: patch.style || 'solid',
                color: patch.color,
                thickness: patch.thickness,
                label: patch.label,
                sourceHandle: patch.sourceHandle,
                targetHandle: patch.targetHandle,
                waypoints: patch.waypoints,
                createdAt: now,
                updatedAt: now,
              };
              await OrgChartEdgeService.save(edge);
              // Sync reports_to so legacy hierarchy lookups still
              // resolve. Only applies when BOTH endpoints are members
              // (team-typed endpoints don't fit the reports_to model).
              if (sourceType === 'member' && targetType === 'member') {
                const target = members.find((x) => x.id === patch.targetMemberId);
                if (target && (!target.reportsTo || target.reportsTo === '__root__')) {
                  await TeamService.save({ ...target, reportsTo: patch.sourceMemberId });
                }
              }
              await loadAll();
            }}
            onUpdateEdge={async (id, patch) => {
              const existing = orgChartEdges.find((e) => e.id === id);
              if (!existing) return;
              await OrgChartEdgeService.save({ ...existing, ...patch });
              await loadAll();
            }}
            onDeleteEdge={async (id) => {
              const edge = orgChartEdges.find((e) => e.id === id);
              if (!edge) return;
              await OrgChartEdgeService.delete(id);
              // reports_to sync only applies for member-to-member edges.
              // Skip the promotion when either endpoint is a team box —
              // team relationships don't have a single reports_to to
              // maintain.
              const memberToMember =
                (edge.sourceType || 'member') === 'member' &&
                (edge.targetType || 'member') === 'member';
              if (memberToMember) {
                const target = members.find((x) => x.id === edge.targetMemberId);
                if (target && target.reportsTo === edge.sourceMemberId) {
                  const remaining = orgChartEdges.filter(
                    (e) =>
                      e.id !== id &&
                      e.targetMemberId === edge.targetMemberId &&
                      (e.sourceType || 'member') === 'member' &&
                      (e.targetType || 'member') === 'member'
                  );
                  const promoted = remaining[0]?.sourceMemberId;
                  await TeamService.save({
                    ...target,
                    reportsTo: promoted || '__root__',
                  });
                }
              }
              await loadAll();
            }}
            canEdit={canEditMembers}
          />
        )}
      </div>

      {/* Member Editor Modal */}
      {editingMember && (
        <MemberEditor
          member={editingMember}
          dirty={memberDirty}
          roles={roles}
          billingRoles={billingRoles}
          departments={departments}
          canSeeRates={hasPermission(authUser?.permissions, 'team_members.view_details')}
          members={members}
          projects={projects}
          memberTeamAssignments={memberTeamAssignments}
          memberProjectAssignments={memberProjectAssignments}
          availableProjects={availableProjects}
          addingProject={addingProject}
          teamName={teamName}
          roleName={roleName}
          onUpdate={updateMember}
          onSave={saveMember}
          onDelete={deleteMember}
          onClose={closeMemberEditor}
          onAssignProject={assignProject}
          onUnassignProject={unassignProject}
          onToggleAddProject={() => setAddingProject((v) => !v)}
        />
      )}

      {/* Team Editor Modal */}
      {editingTeam && (
        <TeamEditor
          team={editingTeam}
          dirty={teamDirty}
          teamAssignments={editingTeamAssignments}
          availableMembers={availableTeamMembers}
          addingMember={addingTeamMember}
          members={members}
          allTeams={teams}
          memberName={memberName}
          onUpdate={updateTeam}
          onSave={saveTeam}
          onDelete={deleteTeam}
          onClose={closeTeamEditor}
          onAddMember={addMemberToTeam}
          onRemoveMember={removeMemberFromTeam}
          onChangeRole={changeTeamMemberRole}
          onToggleAddMember={() => setAddingTeamMember((v) => !v)}
        />
      )}
    </div>
  );
}

// ─── Org Chart (React Flow + dagre) ───

interface OrgChartProps {
  members: TeamMember[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  /** Departments — needed for the dept-pill on each PersonNode (shows
   *  abbreviation + dept color alongside team affinity). */
  departments: Department[];
  roles: Role[];
  billingRoles: BillingRole[];
  /** Visual edges, loaded from `org_chart_edges`. Each row carries its own
   *  style/color/label/waypoints — decoupled from reports_to. */
  orgChartEdges: OrgChartEdge[];
  /** Junction nodes — small connectable hubs the user adds to
   *  consolidate many-to-one or one-to-many relationships. */
  orgChartJunctions: OrgChartJunction[];
  onCreateJunction: (x: number, y: number) => Promise<OrgChartJunction | undefined>;
  onUpdateJunction: (id: string, patch: Partial<OrgChartJunction>) => Promise<void>;
  /** Delete a junction; ALSO drops every edge that references it. */
  onDeleteJunction: (id: string) => Promise<void>;
  roleName: (m: TeamMember) => string;
  onMemberClick: (m: TeamMember) => void;
  onReportsToChange: (memberId: string, newManagerId: string | undefined) => void;
  onTeamAssign: (memberId: string, teamId: string) => void;
  onTeamRemove: (memberId: string, teamId: string) => void;
  onToggleTeamRole: (memberId: string, teamId: string) => void;
  onInsertBetween: (memberId: string, parentId: string, childId: string) => void;
  onMakeParentOf: (memberId: string, childNodeId: string) => void;
  onRemoveFromChart: (memberId: string) => void;
  onDeleteMember: (memberId: string) => void;
  onAddToChart: (memberId: string, x: number, y: number) => void;
  onSavePositions: (updates: Array<{ id: string; orgX: number; orgY: number }>) => void;
  /** Create a visible edge. Parent also syncs reports_to to source when
   *  the target has no primary manager yet. */
  onCreateEdge: (edge: Partial<OrgChartEdge>) => Promise<void>;
  /** Patch any subset of an edge's fields. */
  onUpdateEdge: (id: string, patch: Partial<OrgChartEdge>) => Promise<void>;
  /** Delete an edge. Parent also re-points reports_to (promotes another
   *  edge to primary, or clears) when the deleted edge was the primary. */
  onDeleteEdge: (id: string) => Promise<void>;
  /** When false the chart is rendered view-only: pan / zoom / click-through
   *  to a member detail, but no drags / connections / edits / deletions.
   *  Gated on the `team_members.edit` permission. */
  canEdit: boolean;
}

// ─── Dagre layout helper ───

const NODE_WIDTH = 160;
const NODE_HEIGHT = 80;

function getLayoutedElements<T extends Node>(nodes: T[], edges: Edge[]): T[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 100, marginx: 40, marginy: 40 });

  for (const node of nodes) {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    return { ...node, position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } } as T;
  });
}

// ─── Remote cursors (live multiplayer presence on the org chart) ───
// Renders one cursor + name-tag per other firm member who is currently
// looking at this chart. Coords arrive in flow space; we transform to
// screen space via the live viewport so cursors stay anchored as the
// local user pans / zooms independently.

function userColor(userId: string): string {
  // Deterministic hue per userId — same person always gets the same
  // color across sessions and clients. Golden-ratio hash spreads
  // adjacent ids well across the wheel.
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return `hsl(${h % 360}, 65%, 50%)`;
}

function RemoteCursors({
  cursors,
}: {
  cursors: Record<string, { x: number; y: number; userName: string }>;
}) {
  const { x: vpX, y: vpY, zoom } = useViewport();
  const entries = Object.entries(cursors);
  if (entries.length === 0) return null;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1001,
        overflow: 'hidden',
      }}
    >
      {entries.map(([userId, c]) => {
        const screenX = c.x * zoom + vpX;
        const screenY = c.y * zoom + vpY;
        const color = userColor(userId);
        return (
          <div
            key={userId}
            style={{
              position: 'absolute',
              left: screenX,
              top: screenY,
              transition: 'left 80ms linear, top 80ms linear',
              willChange: 'left, top',
            }}
          >
            <svg
              width="20"
              height="22"
              viewBox="0 0 20 22"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))', display: 'block' }}
            >
              <path
                d="M 2,2 L 2,17 L 6.5,13 L 9.5,20 L 11.5,19 L 8.5,12 L 15,12 Z"
                fill={color}
                stroke="white"
                strokeWidth="1.5"
              />
            </svg>
            <div
              style={{
                background: color,
                color: 'white',
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 4,
                marginTop: 2,
                marginLeft: 4,
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                letterSpacing: '-0.005em',
              }}
            >
              {c.userName}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Snap guide lines (rendered inside ReactFlow viewport) ───

function SnapGuideLines({ hY, vX }: { hY?: number; vX?: number }) {
  const { x: vpX, y: vpY, zoom } = useViewport();
  if (hY === undefined && vX === undefined) return null;

  // Convert flow coordinates to screen-space pixels
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {hY !== undefined && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: hY * zoom + vpY,
            height: 1,
            borderTop: '1px dashed var(--ac)',
            opacity: 0.6,
          }}
        />
      )}
      {vX !== undefined && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: vX * zoom + vpX,
            width: 1,
            borderLeft: '1px dashed var(--ac)',
            opacity: 0.6,
          }}
        />
      )}
    </div>
  );
}

// ─── OrgEdge: custom React Flow edge with style switching + draggable waypoints ───
//
// Why a custom edge: React Flow ships smoothstep/step/straight/bezier paths
// but none of them support user-placed waypoints. For a true org chart UX
// (Lucidchart-style) we need both:
//   • A single global routing style (rounded 90°, 90°, straight, curved)
//   • Per-edge waypoints the user can drag to force the line through points
//
// Waypoints live on the TARGET member (team_members.org_waypoints), since
// reportsTo is single-valued so each member has at most one inbound edge.
//
// Interaction model:
//   • Hover the line → thickens + cursor:pointer (interaction halo path)
//   • Click → selects edge; reveals waypoint handles (drag to move,
//     double-click to remove) and midpoint adders (drag to insert+move).
//   • Path auto-rebuilds on every change so users see live feedback.

/** Routing curve shape — global toolbar choice, per-device. */
export type OrgEdgeRouting = 'smoothstep' | 'step' | 'straight' | 'bezier';
/** Line style — per-edge user pick. */
export type OrgEdgeLineStyle = 'solid' | 'dashed' | 'dotted';

export interface OrgEdgeData extends Record<string, unknown> {
  /** Persistent edge id from `org_chart_edges.id`. Used as the React Flow
   *  edge id too so React Flow's reconnect / delete handlers can address
   *  the DB row directly. */
  edgeId?: string;
  waypoints?: { x: number; y: number }[];
  /** Routing shape (rounded / square / straight / curved) — global. */
  routing?: OrgEdgeRouting;
  /** Per-edge line style — user pick, defaults to 'solid'. */
  lineStyle?: OrgEdgeLineStyle;
  /** Optional accent color (CSS color), falls back to var(--bd). */
  color?: string;
  /** Optional inline label rendered as a chip mid-edge. */
  label?: string;
  onWaypointsChange?: (edgeId: string, next: { x: number; y: number }[]) => void;
  sourceMemberId?: string;
  targetMemberId?: string;
  /** When false the waypoint + midpoint handles never render — viewers
   *  see lines but can't bend them. */
  canEdit?: boolean;
}

function buildOrgPath(
  points: { x: number; y: number }[],
  style: OrgEdgeRouting,
  sourcePosition: Position = Position.Bottom,
  targetPosition: Position = Position.Top
): string {
  if (points.length < 2) return '';
  if (style === 'straight') {
    return (
      `M ${points[0].x},${points[0].y} ` +
      points
        .slice(1)
        .map((p) => `L ${p.x},${p.y}`)
        .join(' ')
    );
  }
  if (style === 'bezier') {
    // Cubic between each pair with control handles half-way in the Y axis —
    // gives a soft S-curve that still reads as a parent→child connection.
    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const cpY = (a.y + b.y) / 2;
      d += ` C ${a.x},${cpY} ${b.x},${cpY} ${b.x},${b.y}`;
    }
    return d;
  }
  // Orthogonal routing. For the simple 2-point case (no waypoints —
  // this is now the dominant case after junctions took over), the
  // route bends along whichever axis matches the source / target
  // handle directions:
  //   • Both top/bottom    → vertical S-bend through midY
  //   • Both left/right    → horizontal S-bend through midX
  //   • Vertical + horiz   → single L-bend through the corner
  // For paths with intermediate waypoints (legacy data only) we fall
  // back to the per-pair midY rule used pre-v2.13.13.
  const radius = style === 'smoothstep' ? 8 : 0;
  if (points.length === 2) {
    return buildOrthogonalSeg(points[0], points[1], sourcePosition, targetPosition, radius);
  }
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (Math.abs(dx) < 0.5 || Math.abs(dy) < 0.5) {
      d += ` L ${b.x},${b.y}`;
      continue;
    }
    const midY = (a.y + b.y) / 2;
    const sgnX = Math.sign(dx);
    const sgnY = Math.sign(dy);
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx) / 2, Math.abs(dy) / 2);
      d += ` L ${a.x},${midY - r * sgnY}`;
      d += ` Q ${a.x},${midY} ${a.x + r * sgnX},${midY}`;
      d += ` L ${b.x - r * sgnX},${midY}`;
      d += ` Q ${b.x},${midY} ${b.x},${midY + r * sgnY}`;
      d += ` L ${b.x},${b.y}`;
    } else {
      d += ` L ${a.x},${midY} L ${b.x},${midY} L ${b.x},${b.y}`;
    }
  }
  return d;
}

// Build a single orthogonal segment between two points, respecting
// the exit / entry direction implied by each end's handle Position.
// Direction logic:
//   • sourcePos top/bottom → first segment is vertical
//   • sourcePos left/right → first segment is horizontal
// Same for the target (last segment). When both directions match
// axis we get an S-bend; when they're perpendicular we get a single
// L-bend; when the points are already axis-aligned we just draw a
// straight line.
function buildOrthogonalSeg(
  a: { x: number; y: number },
  b: { x: number; y: number },
  sourcePos: Position,
  targetPos: Position,
  radius: number
): string {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) < 0.5 || Math.abs(dy) < 0.5) {
    return `M ${a.x},${a.y} L ${b.x},${b.y}`;
  }
  const sgnX = Math.sign(dx);
  const sgnY = Math.sign(dy);
  const sourceVertical = sourcePos === Position.Top || sourcePos === Position.Bottom;
  const targetVertical = targetPos === Position.Top || targetPos === Position.Bottom;
  if (sourceVertical && targetVertical) {
    // V → H → V through midY.
    const midY = (a.y + b.y) / 2;
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx) / 2, Math.abs(dy) / 2);
      return (
        `M ${a.x},${a.y} L ${a.x},${midY - r * sgnY} Q ${a.x},${midY} ${a.x + r * sgnX},${midY} ` +
        `L ${b.x - r * sgnX},${midY} Q ${b.x},${midY} ${b.x},${midY + r * sgnY} L ${b.x},${b.y}`
      );
    }
    return `M ${a.x},${a.y} L ${a.x},${midY} L ${b.x},${midY} L ${b.x},${b.y}`;
  }
  if (!sourceVertical && !targetVertical) {
    // H → V → H through midX.
    const midX = (a.x + b.x) / 2;
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx) / 2, Math.abs(dy) / 2);
      return (
        `M ${a.x},${a.y} L ${midX - r * sgnX},${a.y} Q ${midX},${a.y} ${midX},${a.y + r * sgnY} ` +
        `L ${midX},${b.y - r * sgnY} Q ${midX},${b.y} ${midX + r * sgnX},${b.y} L ${b.x},${b.y}`
      );
    }
    return `M ${a.x},${a.y} L ${midX},${a.y} L ${midX},${b.y} L ${b.x},${b.y}`;
  }
  if (sourceVertical && !targetVertical) {
    // V → H, corner at (a.x, b.y).
    if (radius > 0) {
      const r = Math.min(radius, Math.abs(dx), Math.abs(dy));
      return (
        `M ${a.x},${a.y} L ${a.x},${b.y - r * sgnY} Q ${a.x},${b.y} ${a.x + r * sgnX},${b.y} ` +
        `L ${b.x},${b.y}`
      );
    }
    return `M ${a.x},${a.y} L ${a.x},${b.y} L ${b.x},${b.y}`;
  }
  // Source horizontal, target vertical: corner at (b.x, a.y).
  if (radius > 0) {
    const r = Math.min(radius, Math.abs(dx), Math.abs(dy));
    return (
      `M ${a.x},${a.y} L ${b.x - r * sgnX},${a.y} Q ${b.x},${a.y} ${b.x},${a.y + r * sgnY} ` +
      `L ${b.x},${b.y}`
    );
  }
  return `M ${a.x},${a.y} L ${b.x},${a.y} L ${b.x},${b.y}`;
}

// ─── Logical path model used for the new drag UX ──────────────────────
// A line is rendered as a sequence of orthogonal segments connected at
// corners. Some corners come straight from `waypoints` (real); others
// are synthetic elbows the orthogonal router inserts between two
// non-aligned waypoints. The model distinguishes the two so:
//   • Dragging a real corner edits that waypoint in place.
//   • Dragging a synthetic corner inserts a new waypoint at that index.
//   • Dragging a horizontal segment shifts its Y; dragging a vertical
//     segment shifts its X (perpendicular-only).
//   • Endpoints that map to source / target are never moved — segments
//     anchored at a node only allow inserts on the synthetic side.
type EndpointRef =
  | number /* waypoint index */
  | -1 /* source */
  | -2 /* target */
  | null /* synthetic */;
type SegInfo = {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  axis: 'H' | 'V' | 'D';
  aRef: EndpointRef;
  bRef: EndpointRef;
  /** Index in `waypoints` where a NEW waypoint should be inserted to
   *  realize this segment's a-side (or b-side). For real endpoints it's
   *  redundant; for source it's 0; for target it's waypoints.length;
   *  for synthetic elbows it's the pair's position. */
  aInsertIdx: number;
  bInsertIdx: number;
};
type CornerInfo = {
  x: number;
  y: number;
  /** null = synthetic elbow (drag inserts as new waypoint). Otherwise
   *  the index in `waypoints` that this corner already represents. */
  ref: number | null;
  /** Where in `waypoints` to insert when the corner is synthetic. */
  insertIdx: number;
};

function OrgEdge(props: EdgeProps) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected,
    style,
    markerEnd,
  } = props;
  const d = (data || {}) as OrgEdgeData;
  const waypoints = d.waypoints || [];
  const routing: OrgEdgeRouting = d.routing || 'smoothstep';

  // Lines are now passive — they just render the path between their
  // endpoints (and through any legacy waypoints). To bend a line, the
  // user adds a JUNCTION node and reroutes through it. The earlier
  // per-line waypoint editing UI (segment-drag, synthetic corner
  // dots, perpendicular drag) is gone — it kept confusing users with
  // every-drag-spawns-a-control-point and "what's a control point"
  // questions. Right-click the line for style / Reset routing / label
  // / delete via the OrgChart-level ctx menu.
  const allPoints = [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }];
  // Pass the handle directions through so the router bends along the
  // correct axis. Without this, a line attached to a left/right
  // handle still routes as if it exited vertically — the v2.13.12 bug.
  const pathD = buildOrgPath(allPoints, routing, sourcePosition, targetPosition);

  const stroke = d.color || (style as React.CSSProperties)?.stroke || 'var(--bd)';
  const strokeWidth = selected ? 2.5 : 1.5;
  const lineStyle: OrgEdgeLineStyle = d.lineStyle || 'solid';
  const dashArray = lineStyle === 'dashed' ? '8 4' : lineStyle === 'dotted' ? '2 4' : undefined;
  // Label position — midpoint of the polyline.
  const midIdx = Math.floor(allPoints.length / 2);
  const midA = allPoints[midIdx - 1] || allPoints[0];
  const midB = allPoints[midIdx] || allPoints[allPoints.length - 1];
  const labelX = (midA.x + midB.x) / 2;
  const labelY = (midA.y + midB.y) / 2;

  return (
    <>
      {/* Wide invisible halo so click / right-click / hover hit the
          line easily — visible stroke can be hairline. */}
      <path
        d={pathD}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: 'pointer' }}
      />
      {/* Visible line. */}
      <path
        id={id}
        d={pathD}
        fill="none"
        stroke={selected ? 'var(--ac)' : stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={dashArray}
        markerEnd={markerEnd}
        style={{ transition: 'stroke 120ms ease', pointerEvents: 'none' }}
      />
      {/* Inline label — pill rendered mid-edge. */}
      {d.label && (
        <g style={{ pointerEvents: 'none' }}>
          <rect
            x={labelX - (d.label.length * 3.2 + 8)}
            y={labelY - 9}
            width={d.label.length * 6.4 + 16}
            height={18}
            rx={9}
            fill="var(--sf)"
            stroke={selected ? 'var(--ac)' : stroke}
            strokeWidth={1}
          />
          <text
            x={labelX}
            y={labelY + 4}
            textAnchor="middle"
            fontSize={11}
            fontWeight={500}
            fill="var(--tx)"
          >
            {d.label}
          </text>
        </g>
      )}
    </>
  );
}

const edgeTypes: EdgeTypes = { org: OrgEdge };

// ─── PersonNode: custom React Flow node ───

type PersonNodeData = {
  member: TeamMember;
  teamColor: string;
  /** Pills shown under the name. Each carries its own color so the org
   *  chart shows department membership (MEPT, BIM…) AND team affinity
   *  (Gold, Red…) side by side at a glance. */
  pills: { label: string; color: string; kind: 'team' | 'department' }[];
  isLeader: boolean;
  companyBadge?: { code: string; color: string };
};

function PersonNode({ data }: NodeProps<Node<PersonNodeData>>) {
  const { member, teamColor, pills, isLeader, companyBadge } = data;
  const initials = member.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const color = teamColor || 'var(--ac)';

  return (
    <div className="person-node">
      {/* Handles are invisible — visual dots rendered separately below.
          All four are declared as `source` so the user can DRAG a new
          connection from any side. React Flow's ConnectionMode.Loose lets
          source-typed handles also receive incoming drags, so each side
          works as both endpoint type. */}
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      {/* Visual connection dots — only visible on hover via CSS class */}
      <div className="handle-dot handle-dot-top" />
      <div className="handle-dot handle-dot-bottom" />
      <div className="handle-dot handle-dot-left" />
      <div className="handle-dot handle-dot-right" />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          background: 'var(--sf)',
          border: `2px solid ${color}`,
          borderRadius: 10,
          minWidth: 120,
          boxShadow: '0 2px 8px rgba(0,0,0,.08)',
          cursor: 'grab',
          userSelect: 'none',
          opacity: member.active ? 1 : 0.5,
          position: 'relative',
        }}
      >
        {(isLeader || companyBadge) && (
          <div
            style={{
              position: 'absolute',
              top: -8,
              right: -4,
              display: 'flex',
              flexDirection: 'row-reverse',
              gap: 2,
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            {isLeader && (
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                }}
              >
                <span style={{ fontSize: 11, color: 'var(--color-text-inverse)', lineHeight: 1 }}>
                  {'\u2605'}
                </span>
              </div>
            )}
            {companyBadge && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  padding: '2px 5px',
                  borderRadius: 4,
                  lineHeight: 1,
                  background: 'var(--sf)',
                  color: companyBadge.color,
                  border: `1.5px solid ${companyBadge.color}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,.12)',
                }}
              >
                {companyBadge.code}
              </span>
            )}
          </div>
        )}
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            flexShrink: 0,
            background: color + '18',
            border: `2px solid ${color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color }}>{initials}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--tx)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {member.name}
          </div>
          {pills.length > 0 && (
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 1 }}>
              {pills.map((p) => (
                <span
                  key={`${p.kind}:${p.label}`}
                  title={p.kind === 'department' ? `Department: ${p.label}` : `Team: ${p.label}`}
                  style={{
                    fontSize: 11,
                    padding: '1px 5px',
                    borderRadius: 4,
                    fontWeight: 600,
                    background: p.color + '18',
                    color: p.color,
                    border: `1px solid ${p.color}40`,
                  }}
                >
                  {p.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TeamBoxNode: backdrop for visual peer-grouping ───
// Renders a soft team-colored rectangle that sits BEHIND the person
// nodes. Position + size are computed in OrgChartInner from the bounding
// box of each team's on-chart members, so when a team has 2+ members
// the chart shows them visually grouped without needing a new data
// model. The user just creates a Team in the Teams tab and assigns
// people to it.
function hexToRgba(hex: string, alpha: number): string {
  // Accepts CSS-var fallbacks gracefully — if the value isn't a 6-digit
  // hex we just return it (caller's CSS will resolve var(--...)).
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function TeamBoxNode({
  data,
}: NodeProps<Node<{ name: string; color: string; width: number; height: number }>>) {
  const { name, color, width, height } = data;
  return (
    <div
      style={{
        width,
        height,
        border: `2px dashed ${color}`,
        borderRadius: 16,
        background: hexToRgba(color, 0.06),
        position: 'relative',
        // pointer-events: none on the body so clicks pass through to
        // the person nodes that visually live inside this box. Only the
        // handles + name pill are interactive (each opts in below).
        pointerEvents: 'none',
      }}
    >
      {/* Four-sided connection handles so an edge can attach to a
          team box like it would to a person. Source-typed under
          ConnectionMode.Loose so each side works as either endpoint.
          pointerEvents:auto re-opts in on the handles even though the
          parent is set to 'none'. */}
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <div
        style={{
          position: 'absolute',
          top: -10,
          left: 16,
          background: 'var(--sf)',
          padding: '1px 8px',
          fontSize: 11,
          fontWeight: 600,
          color,
          border: `1px solid ${color}`,
          borderRadius: 999,
          letterSpacing: 0.2,
        }}
      >
        {name}
      </div>
    </div>
  );
}

// ─── JunctionNode: multi-way connection hub ───
// Visually a small circle with four invisible handles so a line can
// attach to any side. The label (if set) renders below the dot. The
// data shape is just position + label + color — everything else
// (drag, snap, delete) reuses the existing React Flow / OrgChart
// machinery so junctions feel exactly like a normal node to drag.
function JunctionNode({ data, selected }: NodeProps<Node<{ label?: string; color?: string }>>) {
  const color = data.color || 'var(--ac)';
  return (
    <div
      style={{
        width: 16,
        height: 16,
        position: 'relative',
        transform: 'translate(-2px, -2px)',
      }}
    >
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ opacity: 0, pointerEvents: 'auto' }}
      />
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: color,
          border: `2px solid ${selected ? 'var(--tx)' : 'white'}`,
          boxShadow: selected ? `0 0 0 2px ${color}` : '0 1px 2px rgba(0,0,0,0.15)',
          margin: 2,
          cursor: 'move',
        }}
      />
      {data.label && (
        <div
          style={{
            position: 'absolute',
            top: 18,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--sf)',
            border: `1px solid ${color}`,
            borderRadius: 999,
            padding: '1px 8px',
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--tx)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {data.label}
        </div>
      )}
    </div>
  );
}

const nodeTypes: NodeTypes = {
  person: PersonNode,
  teamBox: TeamBoxNode,
  junction: JunctionNode,
};

// ─── Token component (used in palette and workspace) ───
const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

function MemberToken({
  member,
  color,
  size = 40,
  style: extra,
  ...rest
}: {
  member: TeamMember;
  color: string;
  size?: number;
  style?: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: color + '20',
        border: `2px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'grab',
        userSelect: 'none',
        position: 'relative',
        ...extra,
      }}
    >
      <span style={{ fontSize: size * 0.35, fontWeight: 700, color, fontFamily: 'inherit' }}>
        {getInitials(member.name)}
      </span>
    </div>
  );
}

function TeamToken({
  team,
  size = 36,
  style: extra,
  ...rest
}: {
  team: Team;
  size?: number;
  style?: React.CSSProperties;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      style={{
        height: size,
        padding: '0 10px',
        borderRadius: 6,
        flexShrink: 0,
        background: (team.color || 'var(--ac)') + '18',
        border: `2px solid ${team.color || 'var(--ac)'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'grab',
        userSelect: 'none',
        ...extra,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: team.color || 'var(--ac)',
          whiteSpace: 'nowrap',
        }}
      >
        {team.name}
      </span>
    </div>
  );
}

/**
 * Interactive Org Chart Workspace — pan, zoom, drag-and-drop with live preview connectors
 */
function OrgChart(props: OrgChartProps) {
  return (
    <ReactFlowProvider>
      <OrgChartInner {...props} />
    </ReactFlowProvider>
  );
}

function OrgChartInner({
  members,
  teams,
  teamAssignments,
  departments,
  billingRoles,
  onMemberClick,
  onReportsToChange,
  onTeamAssign,
  onTeamRemove,
  onToggleTeamRole,
  onRemoveFromChart,
  onDeleteMember,
  onAddToChart,
  onSavePositions,
  orgChartEdges,
  orgChartJunctions,
  onCreateJunction,
  onUpdateJunction,
  onDeleteJunction,
  onCreateEdge,
  onUpdateEdge,
  onDeleteEdge,
  canEdit,
}: OrgChartProps) {
  const [search, setSearch] = useState('');
  const [ctxMenu, setCtxMenu] = useState<{ memberId: string; x: number; y: number } | null>(null);

  // ─── Undo / redo ─────────────────────────────────────────────────
  // Command stack: each entry is something the user did (or its
  // inverse, after an undo). Both Do and Undo persist to the DB so the
  // history survives reload as long as the user is in this session.
  // We don't snapshot style/color/label changes — only structural
  // mutations (node moves, edge create/delete) since those are the
  // ones the user is most likely to want to revert.
  type UndoCmd =
    | {
        kind: 'move';
        updates: { id: string; prevX: number; prevY: number; newX: number; newY: number }[];
      }
    | { kind: 'edge-create'; edge: OrgChartEdge }
    | { kind: 'edge-delete'; edge: OrgChartEdge };
  const undoStack = useRef<UndoCmd[]>([]);
  const redoStack = useRef<UndoCmd[]>([]);
  const pushUndo = useCallback((cmd: UndoCmd) => {
    undoStack.current.push(cmd);
    if (undoStack.current.length > 50) undoStack.current.shift();
    // Any new user action clears the redo stack — same shape as every
    // serious editor.
    redoStack.current = [];
  }, []);
  const paletteRef = useRef<HTMLDivElement>(null);

  // ─── Inject handle dot CSS into document head (bypasses React Flow style overrides) ───
  useEffect(() => {
    const id = 'orgchart-handle-dots-css';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .person-node .handle-dot {
        position: absolute; width: 10px; height: 10px; border-radius: 50%;
        background: var(--bd); border: 2px solid var(--sf);
        opacity: 0; transition: opacity .15s; pointer-events: none; z-index: 10;
      }
      .person-node:hover .handle-dot { opacity: 0.8; }
      .person-node .handle-dot-top { top: -7px; left: 50%; transform: translateX(-50%); }
      .person-node .handle-dot-bottom { bottom: -7px; left: 50%; transform: translateX(-50%); }
      .person-node .handle-dot-left { left: -7px; top: 50%; transform: translateY(-50%); }
      .person-node .handle-dot-right { right: -7px; top: 50%; transform: translateY(-50%); }
    `;
    document.head.appendChild(style);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  // ─── Helpers ───
  function getMemberTeams(mid: string): Team[] {
    return teamAssignments
      .filter((a) => a.teamMemberId === mid)
      .map((a) => teams.find((t) => t.id === a.teamId))
      .filter(Boolean) as Team[];
  }
  function getMemberTeamColor(mid: string): string {
    return getMemberTeams(mid)[0]?.color || 'var(--ac)';
  }

  // ─── Routing preference (per-device, localStorage) ───
  // This is the GLOBAL curve shape (rounded / square / straight / curved).
  // Per-edge line style (solid / dashed / dotted) is set via the inline
  // edge context menu and stored per edge in org_chart_edges.style.
  const ROUTING_KEY = 'specforma:orgchart:routing';
  const [routing, setRoutingState] = useState<OrgEdgeRouting>(() => {
    if (typeof window === 'undefined') return 'smoothstep';
    const v = window.localStorage.getItem(ROUTING_KEY);
    return v === 'step' || v === 'straight' || v === 'bezier' || v === 'smoothstep'
      ? v
      : 'smoothstep';
  });
  const setRouting = useCallback((s: OrgEdgeRouting) => {
    setRoutingState(s);
    try {
      window.localStorage.setItem(ROUTING_KEY, s);
    } catch {}
  }, []);

  // ─── Snap toggle (per-device, localStorage) ─────────────────────
  // Snap aligns dragged nodes / control points to other nodes' edges
  // (left, center, right, top, center, bottom) and to existing
  // waypoints. Toggle off when you want pixel-accurate placement.
  // Default: on.
  const SNAP_KEY = 'specforma:orgchart:snap';
  const [snapEnabled, setSnapEnabledState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(SNAP_KEY) !== 'false';
  });
  const setSnapEnabled = useCallback((v: boolean) => {
    setSnapEnabledState(v);
    try {
      window.localStorage.setItem(SNAP_KEY, v ? 'true' : 'false');
    } catch {}
  }, []);

  // Stable ref used inside drag closures so the toggle's effect is
  // immediate even mid-drag.
  const snapEnabledRef = useRef(snapEnabled);
  useEffect(() => {
    snapEnabledRef.current = snapEnabled;
  }, [snapEnabled]);

  // Window-scoped bridges read by OrgEdge's dragCorner. We can't pass
  // these via React props because the dragCorner closure captures
  // values at pointerdown — using a window ref keeps the toggle live
  // mid-drag and lets the edge see the latest node positions without
  // re-creating the edge component on every node move.
  useEffect(() => {
    (window as unknown as { __orgChartSnapEnabled?: boolean }).__orgChartSnapEnabled = snapEnabled;
  }, [snapEnabled]);

  // ─── Per-team box visibility (per-device, localStorage) ───────────
  // The toolbar surfaces a checklist of every team so users can flip
  // individual backdrops on / off. Off by default — the boxes are
  // visually heavy, so the chart starts clean and the user opts in.
  const TEAM_BOXES_KEY = 'specforma:orgchart:teamBoxes';
  const [visibleTeamIds, setVisibleTeamIdsState] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem(TEAM_BOXES_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      // Migrate the v2.13.7 boolean shape (single global flag) into a
      // map. If the old value was `"true"`, leave the map empty — users
      // can re-enable individual teams via the new dropdown.
      if (typeof parsed === 'object' && parsed) return parsed;
      return {};
    } catch {
      return {};
    }
  });
  const setTeamBoxVisible = useCallback((teamId: string, visible: boolean) => {
    setVisibleTeamIdsState((prev) => {
      const next = { ...prev, [teamId]: visible };
      // Drop falsy entries so the map stays compact.
      if (!visible) delete next[teamId];
      try {
        window.localStorage.setItem(TEAM_BOXES_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);
  const setAllTeamsVisible = useCallback(
    (visible: boolean) => {
      const next: Record<string, boolean> = {};
      if (visible) for (const t of teams) next[t.id] = true;
      setVisibleTeamIdsState(next);
      try {
        window.localStorage.setItem(TEAM_BOXES_KEY, JSON.stringify(next));
      } catch {}
    },
    [teams]
  );
  const anyTeamBoxVisible = Object.values(visibleTeamIds).some(Boolean);
  const [teamBoxMenuOpen, setTeamBoxMenuOpen] = useState(false);
  // Close the team-box dropdown on any outside click. The dropdown
  // itself stops propagation on its own onClick so this only fires
  // when the user clicks elsewhere.
  useEffect(() => {
    if (!teamBoxMenuOpen) return;
    const onDocClick = () => setTeamBoxMenuOpen(false);
    // Defer attaching by a tick so the click that OPENED the menu
    // doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDocClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [teamBoxMenuOpen]);

  // ─── Auto-pick handle pair from geometry (used when no override saved) ───
  // Returns the most natural handle pair given a parent box at (px,py) and
  // a child box at (cx,cy), both 160x80. Picks the vertical axis when |dy|
  // dominates, horizontal otherwise — so the line attaches to the closer
  // side and never crosses through the node.
  function autoPickHandles(
    parent: TeamMember,
    child: TeamMember
  ): { source: 'top' | 'bottom' | 'left' | 'right'; target: 'top' | 'bottom' | 'left' | 'right' } {
    const pX = (parent.orgX ?? 0) + NODE_WIDTH / 2;
    const pY = (parent.orgY ?? 0) + NODE_HEIGHT / 2;
    const cX = (child.orgX ?? 0) + NODE_WIDTH / 2;
    const cY = (child.orgY ?? 0) + NODE_HEIGHT / 2;
    const dx = cX - pX;
    const dy = cY - pY;
    if (Math.abs(dy) >= Math.abs(dx)) {
      return dy >= 0 ? { source: 'bottom', target: 'top' } : { source: 'top', target: 'bottom' };
    }
    return dx >= 0 ? { source: 'right', target: 'left' } : { source: 'left', target: 'right' };
  }

  // ─── Determine on-chart members ───
  // A member is on the chart if they appear on either end of any
  // org_chart_edges row OR are in a team assignment. reports_to is no
  // longer consulted directly — the migration seeded edges from it, and
  // from here on edges are the source of truth for who's on the chart.
  const onChart = new Set<string>();
  for (const e of orgChartEdges) {
    onChart.add(e.sourceMemberId);
    onChart.add(e.targetMemberId);
  }
  for (const m of members) {
    if (m.reportsTo === '__root__' || teamAssignments.some((a) => a.teamMemberId === m.id)) {
      onChart.add(m.id);
    }
  }

  // ─── Build React Flow nodes + edges ───
  const chartMembers = members.filter((m) => onChart.has(m.id));

  const { initialNodes, initialEdges } = useMemo(() => {
    const memberById = new Map(chartMembers.map((m) => [m.id, m]));
    const teamById = new Map(teams.map((t) => [t.id, t]));
    const junctionById = new Map(orgChartJunctions.map((j) => [j.id, j]));
    // Helper: for a (type, id) pair, give back a pseudo-member-shaped
    // record carrying just the position the autoPickHandles needs.
    // Team boxes' positions aren't known until the teamBox node is
    // emitted below, so we have to peek at the same bbox calc here.
    // To keep the code simple we instead defer team-endpoint handle
    // picking to React Flow: it auto-routes when source/targetHandle
    // are unset and the node id resolves to a team-box node.
    // Resolve helpers for the three endpoint kinds.
    const endpointExists = (id: string, kind?: 'member' | 'team' | 'junction') => {
      if (kind === 'team') return teamById.has(id);
      if (kind === 'junction') return junctionById.has(id);
      return memberById.has(id);
    };
    const endpointNodeId = (id: string, kind?: 'member' | 'team' | 'junction') => {
      if (kind === 'team') return `team-box-${id}`;
      if (kind === 'junction') return `junction-${id}`;
      return id;
    };
    const rfEdges: Edge[] = orgChartEdges
      .filter(
        (e) =>
          endpointExists(e.sourceMemberId, e.sourceType as any) &&
          endpointExists(e.targetMemberId, e.targetType as any)
      )
      .map((e) => {
        const sourceId = endpointNodeId(e.sourceMemberId, e.sourceType as any);
        const targetId = endpointNodeId(e.targetMemberId, e.targetType as any);
        // Auto handle pair: only when BOTH ends are member nodes (we
        // know their geometry). Junction/team endpoints get React
        // Flow's default sides.
        let sourceHandle = e.sourceHandle;
        let targetHandle = e.targetHandle;
        if (!sourceHandle || !targetHandle) {
          if ((e.sourceType || 'member') === 'member' && (e.targetType || 'member') === 'member') {
            const parent = memberById.get(e.sourceMemberId)!;
            const child = memberById.get(e.targetMemberId)!;
            const auto = autoPickHandles(parent, child);
            sourceHandle = sourceHandle || auto.source;
            targetHandle = targetHandle || auto.target;
          }
        }
        return {
          id: e.id,
          source: sourceId,
          target: targetId,
          sourceHandle,
          targetHandle,
          type: 'org',
          data: {
            edgeId: e.id,
            waypoints: e.waypoints || [],
            routing,
            lineStyle: e.style || 'solid',
            color: e.color,
            label: e.label,
            sourceMemberId: e.sourceMemberId,
            targetMemberId: e.targetMemberId,
            canEdit,
          } as OrgEdgeData,
          style: { stroke: e.color || 'var(--bd)', strokeWidth: e.thickness || 1.5 },
        } as Edge;
      });

    let rfNodes: Node<PersonNodeData>[] = chartMembers.map((m) => {
      const mTeams = getMemberTeams(m.id);
      const isLeader = teamAssignments.some(
        (a) => a.teamMemberId === m.id && a.teamRole === 'leader'
      );
      // The badge code/color used to come from companyRoles.short_code/
      // badge_color.  After migration 015 those values live on the merged
      // Title (billing_roles.abbreviation/color), so the org-chart badge
      // now reads from the same row that drives "Title" everywhere else.
      const titleRow = m.billingRoleId
        ? billingRoles.find((b) => b.id === m.billingRoleId)
        : undefined;
      // Build the pill list shown under the name. Department first (MEPT,
      // BIM, …) so role/discipline reads at-a-glance; then team(s) (Gold,
      // Red, …) for affinity grouping. Each pill carries its own color
      // sourced from the entity's record so a small set of color-named
      // teams is immediately legible alongside the discipline departments.
      const pills: PersonNodeData['pills'] = [];
      if (m.departmentId) {
        const dept = departments.find((d) => d.id === m.departmentId);
        if (dept) {
          pills.push({
            kind: 'department',
            label: dept.abbreviation || dept.name,
            color: dept.color || 'var(--ac)',
          });
        }
      }
      for (const t of mTeams) {
        pills.push({
          kind: 'team',
          label: t.name,
          color: t.color || getMemberTeamColor(m.id),
        });
      }
      return {
        id: m.id,
        type: 'person' as const,
        data: {
          member: m,
          teamColor: getMemberTeamColor(m.id),
          pills,
          isLeader,
          companyBadge: titleRow?.abbreviation
            ? { code: titleRow.abbreviation, color: titleRow.color || '#4A90D9' }
            : undefined,
        },
        position: m.orgX != null && m.orgY != null ? { x: m.orgX, y: m.orgY } : { x: 0, y: 0 }, // dagre will override
      };
    });

    // Run dagre ONLY for nodes without saved positions — never move existing nodes
    const unsavedIds = new Set(
      chartMembers.filter((m) => m.orgX == null || m.orgY == null).map((m) => m.id)
    );

    if (unsavedIds.size > 0) {
      // Run dagre on full graph to get structurally-correct positions
      const layouted = getLayoutedElements(rfNodes, rfEdges);
      // Only apply dagre positions to nodes that don't have saved positions
      rfNodes = rfNodes.map((n) =>
        unsavedIds.has(n.id)
          ? { ...n, position: layouted.find((ln) => ln.id === n.id)?.position ?? n.position }
          : n
      );
      // Auto-save the new positions so this only happens once
      const updates = rfNodes
        .filter((n) => unsavedIds.has(n.id))
        .map((n) => ({ id: n.id, orgX: n.position.x, orgY: n.position.y }));
      if (updates.length > 0) setTimeout(() => onSavePositions(updates), 100);
    }

    // Team-box backdrop nodes are NOT generated here — they live in a
    // dedicated effect below that watches the LIVE node positions, so
    // dragging a member or toggling a team visibility updates the box
    // extent instantly (instead of waiting for a page reload).

    // Junction nodes — small connectable hubs. Rendered AFTER person
    // nodes so they sit on top (z-order = DOM order in React Flow).
    // Their positions are stored directly on the row; drag-to-save
    // routes through onUpdateJunction.
    const junctionNodes: Node[] = orgChartJunctions.map((j) => ({
      id: `junction-${j.id}`,
      type: 'junction',
      position: { x: j.x, y: j.y },
      data: { label: j.label, color: j.color },
      draggable: canEdit,
      connectable: canEdit,
      selectable: true,
      zIndex: 5,
    }));

    return {
      initialNodes: [...rfNodes, ...junctionNodes],
      initialEdges: rfEdges,
    };
  }, [
    chartMembers.map((m) => m.id).join(','),
    // Edge identity + style key — re-derive when any edge field changes.
    orgChartEdges
      .map(
        (e) =>
          `${e.id}:${e.sourceMemberId}:${e.targetMemberId}:${e.sourceType || ''}:${e.targetType || ''}:${e.style || ''}:${e.color || ''}:${e.label || ''}:${e.sourceHandle || ''}:${e.targetHandle || ''}:${(e.waypoints || []).length}`
      )
      .join('|'),
    // Junction identity + position key — re-derive when junctions
    // change so React Flow nodes reflect the latest positions.
    orgChartJunctions
      .map((j) => `${j.id}:${j.x}:${j.y}:${j.label || ''}:${j.color || ''}`)
      .join('|'),
    routing,
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Push node-derived snap targets into the window bridge so
  // OrgEdge's dragCorner can pick them up. Updated whenever nodes
  // settle (not during every drag tick — that would thrash). The
  // snap target list is "every person node's left/center/right and
  // top/center/bottom" plus every existing waypoint's x/y.
  useEffect(() => {
    const xs: number[] = [];
    const ys: number[] = [];
    for (const n of nodes) {
      if (n.type !== 'person') continue;
      const nw = n.measured?.width ?? NODE_WIDTH;
      const nh = n.measured?.height ?? NODE_HEIGHT;
      xs.push(n.position.x, n.position.x + nw / 2, n.position.x + nw);
      ys.push(n.position.y, n.position.y + nh / 2, n.position.y + nh);
    }
    for (const e of orgChartEdges) {
      for (const wp of e.waypoints || []) {
        xs.push(wp.x);
        ys.push(wp.y);
      }
    }
    (
      window as unknown as { __orgChartSnapTargets?: { xs: number[]; ys: number[] } }
    ).__orgChartSnapTargets = { xs, ys };
  }, [nodes, orgChartEdges]);

  // ─── Live team-box backdrops ───
  // Recomputes the team-box bounding rects from the LIVE `nodes` state
  // every render (toggling a team, dragging a member, adding / removing
  // a team assignment). A signature compare prevents the setNodes call
  // from looping the effect when nothing actually changed.
  useEffect(() => {
    const PAD = 24;
    const HEADER = 14;
    const personNodes = nodes.filter((n) => n.type === 'person');
    const newBoxes: Node[] = [];
    for (const team of teams) {
      // Checkbox is authoritative. If the user explicitly turns a team
      // box off it stays off, even when an edge points at it. Any edge
      // whose target/source is this hidden team will simply not render
      // until the box is toggled back on — self-correcting feedback:
      // re-enable the box to recover the line.
      if (!visibleTeamIds[team.id]) continue;
      const memberIdsInTeam = teamAssignments
        .filter((a) => a.teamId === team.id)
        .map((a) => a.teamMemberId);
      const teamNodes = personNodes.filter((n) => memberIdsInTeam.includes(n.id));
      if (teamNodes.length < 2) continue;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const n of teamNodes) {
        const w = n.measured?.width ?? NODE_WIDTH;
        const h = n.measured?.height ?? NODE_HEIGHT;
        minX = Math.min(minX, n.position.x);
        minY = Math.min(minY, n.position.y);
        maxX = Math.max(maxX, n.position.x + w);
        maxY = Math.max(maxY, n.position.y + h);
      }
      newBoxes.push({
        id: `team-box-${team.id}`,
        type: 'teamBox',
        // Team boxes are draggable for editors — drag moves every
        // member inside the team as a group (delta-applied to each
        // person node). View-only users keep boxes pinned.
        draggable: canEdit,
        selectable: false,
        focusable: false,
        connectable: true,
        zIndex: -1,
        position: { x: minX - PAD, y: minY - PAD - HEADER },
        data: {
          name: team.name,
          color: team.color || 'var(--ac)',
          width: maxX - minX + PAD * 2,
          height: maxY - minY + PAD * 2 + HEADER,
        },
      } as Node);
    }
    const sig = (boxes: Node[]) =>
      boxes
        .map((b) => {
          const d = (b.data || {}) as {
            width?: number;
            height?: number;
            name?: string;
            color?: string;
          };
          return `${b.id}:${b.position.x}:${b.position.y}:${d.width}:${d.height}:${d.name}:${d.color}`;
        })
        .sort()
        .join('|');
    const existing = nodes.filter((n) => n.type === 'teamBox');
    if (sig(existing) === sig(newBoxes)) return;
    setNodes((prev) => {
      const without = prev.filter((n) => n.type !== 'teamBox');
      // Team boxes go FIRST so React Flow renders them behind person
      // nodes (z-order = DOM order).
      return [...newBoxes, ...without];
    });
  }, [nodes, visibleTeamIds, teamAssignments, teams, setNodes, canEdit]);

  // ─── Waypoint editing ───
  // The OrgEdge component calls this on every pointermove (live drag) AND
  // again on pointerup (final commit). The UI update is immediate so the
  // line follows the cursor; the DB write is debounced so we don't fire a
  // save per pixel.
  const waypointSaveTimer = useRef<Record<string, any>>({});
  const handleWaypointsChange = useCallback(
    (edgeId: string, next: { x: number; y: number }[]) => {
      // Live UI update first so the line follows the cursor.
      setEdges((prev) =>
        prev.map((e) => {
          if (e.id !== edgeId) return e;
          const d = (e.data || {}) as OrgEdgeData;
          return { ...e, data: { ...d, waypoints: next } };
        })
      );
      // Debounced persistence — keyed by edgeId so concurrent edits to
      // different lines don't stomp each other.
      if (waypointSaveTimer.current[edgeId]) clearTimeout(waypointSaveTimer.current[edgeId]);
      waypointSaveTimer.current[edgeId] = setTimeout(() => {
        onUpdateEdge(edgeId, { waypoints: next.length > 0 ? next : undefined });
        delete waypointSaveTimer.current[edgeId];
      }, 250);
    },
    [setEdges, onUpdateEdge]
  );

  // Inject the live onWaypointsChange callback into every edge's `data`
  // every time `edges` is re-derived from initialEdges. Done as an effect
  // (not in the memo above) because the callback closes over setEdges/
  // onSaveEdgeMeta which would otherwise create stale closures.
  useEffect(() => {
    setEdges((prev) =>
      prev.map((e) => {
        if (e.type !== 'org') return e;
        const d = (e.data || {}) as OrgEdgeData;
        if (
          d.onWaypointsChange === handleWaypointsChange &&
          d.routing === routing &&
          d.canEdit === canEdit
        )
          return e;
        return {
          ...e,
          data: {
            ...d,
            onWaypointsChange: canEdit ? handleWaypointsChange : undefined,
            routing,
            canEdit,
          },
        };
      })
    );
  }, [handleWaypointsChange, routing, canEdit, setEdges]);

  // Track which member IDs + hierarchy are on chart so we only re-derive when that changes
  const prevChartKey = useRef('');
  useEffect(() => {
    const chartKey =
      chartMembers
        .map((m) => m.id)
        .sort()
        .join('|') +
      '|e:' +
      orgChartEdges
        .map(
          (e) =>
            `${e.id}:${e.sourceMemberId}:${e.targetMemberId}:${e.sourceType || ''}:${e.targetType || ''}:${e.style || ''}:${e.color || ''}:${e.label || ''}:${e.sourceHandle || ''}:${e.targetHandle || ''}`
        )
        .sort()
        .join(',') +
      '|j:' +
      orgChartJunctions
        .map((j) => `${j.id}:${j.x}:${j.y}:${j.label || ''}:${j.color || ''}`)
        .sort()
        .join(',') +
      '|ta:' +
      teamAssignments
        .map((a) => `${a.teamMemberId}-${a.teamId}`)
        .sort()
        .join(',');
    if (chartKey !== prevChartKey.current) {
      prevChartKey.current = chartKey;
      setNodes(initialNodes);
      setEdges(initialEdges);
    }
  }, [initialNodes, initialEdges]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Snap alignment (Revit-style guide lines) ───
  const SNAP_THRESHOLD = 8;
  const [snapLines, setSnapLines] = useState<{ hY?: number; vX?: number }>({});
  const { getNodes, setNodes: rfSetNodes, screenToFlowPosition } = useReactFlow();
  // Refs for snap state (avoids stale closure in onNodeDragStop)
  const snapActive = useRef(false);
  const snapPositions = useRef<Record<string, { x: number; y: number }>>({});

  // ─── Live multiplayer state ───
  // Node IDs the local user is currently dragging — used to ignore
  // incoming remote drag updates for those IDs so a remote echo can't
  // fight the local drag.
  const localDraggingIds = useRef<Set<string>>(new Set());
  // Throttles for outbound socket sends. 33ms ≈ 30 fps for drag,
  // 50ms ≈ 20 fps for cursor — fluid without spamming the socket.
  const lastDragSendAt = useRef(0);
  const lastCursorSendAt = useRef(0);
  // TTL timers per remote user so silent senders auto-clear.
  const remoteCursorTtl = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Live cursors from other firm members on this chart.
  const [remoteCursors, setRemoteCursors] = useState<
    Record<string, { x: number; y: number; userName: string }>
  >({});

  // ─── Team-box drag state ───
  // When a user drags the team-box backdrop the whole team moves as a
  // group — every member node shifts by the same delta as the box. We
  // capture each member's starting position at dragStart so dx/dy can
  // be applied without compounding rounding errors over a multi-frame
  // drag.
  const teamDragStart = useRef<{
    teamBoxStartPos: { x: number; y: number };
    memberStartPositions: Map<string, { x: number; y: number }>;
  } | null>(null);

  const onNodeDragStart: OnNodeDrag = useCallback(
    (_event, node, allDraggedNodes) => {
      for (const n of allDraggedNodes) localDraggingIds.current.add(n.id);
      // Team-box drag: snapshot member start positions so onNodeDrag
      // can apply a clean dx/dy without drift. Also mark the members
      // as locally-dragging so incoming remote echoes for them are
      // ignored mid-drag.
      if (node.type === 'teamBox') {
        const teamId = node.id.replace(/^team-box-/, '');
        const memberIds = teamAssignments
          .filter((a) => a.teamId === teamId)
          .map((a) => a.teamMemberId);
        const startPositions = new Map<string, { x: number; y: number }>();
        for (const n of getNodes()) {
          if (n.type === 'person' && memberIds.includes(n.id)) {
            startPositions.set(n.id, { x: n.position.x, y: n.position.y });
            localDraggingIds.current.add(n.id);
          }
        }
        teamDragStart.current = {
          teamBoxStartPos: { x: node.position.x, y: node.position.y },
          memberStartPositions: startPositions,
        };
      }
    },
    [teamAssignments, getNodes]
  );

  const onNodeDrag: OnNodeDrag = useCallback(
    (_event, draggedNode, allDraggedNodes) => {
      // Team-box drag: move every member by the box's delta. We hand
      // back early because snap logic and per-frame snapPositions
      // tracking don't apply to a group drag — the box itself is
      // never persisted, only its members.
      const groupDrag = teamDragStart.current;
      if (groupDrag && draggedNode.type === 'teamBox') {
        const dx = draggedNode.position.x - groupDrag.teamBoxStartPos.x;
        const dy = draggedNode.position.y - groupDrag.teamBoxStartPos.y;
        rfSetNodes((nds) =>
          nds.map((n) => {
            const start = groupDrag.memberStartPositions.get(n.id);
            if (!start) return n;
            return { ...n, position: { x: start.x + dx, y: start.y + dy } };
          })
        );
        // Throttled live broadcast of all moved members.
        const groupNow = performance.now();
        if (groupNow - lastDragSendAt.current >= 33) {
          lastDragSendAt.current = groupNow;
          const payload: Array<{
            id: string;
            x: number;
            y: number;
            type: 'person' | 'junction';
          }> = [];
          for (const [id, start] of groupDrag.memberStartPositions.entries()) {
            payload.push({ id, x: start.x + dx, y: start.y + dy, type: 'person' });
          }
          sendOrgChartDrag(payload, true);
        }
        return;
      }

      // Live broadcast — runs regardless of snap state so remote
      // viewers see every motion of every selected node, not just
      // when the local user happens to snap. Throttled to 30 fps so
      // a fast drag doesn't flood the WebSocket.
      const tNow = performance.now();
      if (tNow - lastDragSendAt.current >= 33) {
        lastDragSendAt.current = tNow;
        const payload = allDraggedNodes.map((n) => {
          // Use the snapped position when available so remote viewers
          // see the same final landing the local user does, not the
          // raw "snapped-back-and-forth" jitter.
          const sp = snapPositions.current[n.id];
          return {
            id: n.id,
            x: sp?.x ?? n.position.x,
            y: sp?.y ?? n.position.y,
            type: (n.type === 'junction' ? 'junction' : 'person') as 'person' | 'junction',
          };
        });
        sendOrgChartDrag(payload, true);
      }

      // Toggle-gated; users can turn snap off entirely.
      if (!snapEnabledRef.current) {
        snapActive.current = false;
        setSnapLines({});
        delete snapPositions.current[draggedNode.id];
        return;
      }
      const allNodes = getNodes();
      const w = draggedNode.measured?.width ?? NODE_WIDTH;
      const h = draggedNode.measured?.height ?? NODE_HEIGHT;
      const dL = draggedNode.position.x;
      const dCx = draggedNode.position.x + w / 2;
      const dR = draggedNode.position.x + w;
      const dT = draggedNode.position.y;
      const dCy = draggedNode.position.y + h / 2;
      const dB = draggedNode.position.y + h;
      const xCands: Array<[number, number]> = [
        [dL, 0],
        [dCx, w / 2],
        [dR, w],
      ];
      const yCands: Array<[number, number]> = [
        [dT, 0],
        [dCy, h / 2],
        [dB, h],
      ];

      // Correlated snap: for each candidate target, compute the best
      // X+Y snap considering ONLY that target's own coordinates. This
      // prevents the "phantom corner" bug where X from one node and Y
      // from another would combine into a snap position that doesn't
      // actually align with anything visible on screen.
      //
      // Scoring rule: a target where BOTH axes engage scores by
      // (xDist + yDist). A target where only one axis engages adds
      // SINGLE_AXIS_PENALTY so a clean single-axis alignment with
      // node A loses to a dual-axis alignment with node B — the user
      // sees the line snap to a real, visible target, not a phantom.
      const SINGLE_AXIS_PENALTY = 1000;
      type BestSnap = {
        xTarget?: number;
        xOffset: number;
        yTarget?: number;
        yOffset: number;
        score: number;
      };
      let best: BestSnap | null = null;

      function considerTarget(targetX: number[], targetY: number[]): void {
        let bxDist = SNAP_THRESHOLD;
        let bxTarget: number | undefined;
        let bxOffset = 0;
        for (const [cand, off] of xCands) {
          for (const t of targetX) {
            const d = Math.abs(cand - t);
            if (d < bxDist) {
              bxDist = d;
              bxTarget = t;
              bxOffset = off;
            }
          }
        }
        let byDist = SNAP_THRESHOLD;
        let byTarget: number | undefined;
        let byOffset = 0;
        for (const [cand, off] of yCands) {
          for (const t of targetY) {
            const d = Math.abs(cand - t);
            if (d < byDist) {
              byDist = d;
              byTarget = t;
              byOffset = off;
            }
          }
        }
        if (bxTarget === undefined && byTarget === undefined) return;
        const score =
          bxTarget !== undefined && byTarget !== undefined
            ? bxDist + byDist
            : (bxTarget !== undefined ? bxDist : byDist) + SINGLE_AXIS_PENALTY;
        if (!best || score < best.score) {
          best = {
            xTarget: bxTarget,
            xOffset: bxOffset,
            yTarget: byTarget,
            yOffset: byOffset,
            score,
          };
        }
      }

      // Each person target contributes 3 X edges + 3 Y edges from the
      // same source node — they have to be evaluated together.
      for (const n of allNodes) {
        if (n.id === draggedNode.id) continue;
        if (n.type !== 'person') continue;
        const nw = n.measured?.width ?? NODE_WIDTH;
        const nh = n.measured?.height ?? NODE_HEIGHT;
        considerTarget(
          [n.position.x, n.position.x + nw / 2, n.position.x + nw],
          [n.position.y, n.position.y + nh / 2, n.position.y + nh]
        );
      }

      // Each waypoint is a single point — one X paired with one Y.
      for (const e of orgChartEdges) {
        for (const wp of e.waypoints || []) {
          considerTarget([wp.x], [wp.y]);
        }
      }

      // Cast away the closure-mutation narrowing: TS doesn't track
      // assignments to `best` inside `considerTarget`, so it stays
      // typed as the initial `null`. Coerce back to the real type.
      const bestSnap = best as BestSnap | null;
      if (!bestSnap) {
        snapActive.current = false;
        setSnapLines({});
        delete snapPositions.current[draggedNode.id];
        return;
      }

      const snappedX =
        bestSnap.xTarget !== undefined
          ? bestSnap.xTarget - bestSnap.xOffset
          : draggedNode.position.x;
      const snappedY =
        bestSnap.yTarget !== undefined
          ? bestSnap.yTarget - bestSnap.yOffset
          : draggedNode.position.y;
      snapActive.current = true;
      setSnapLines({ hY: bestSnap.yTarget, vX: bestSnap.xTarget });
      snapPositions.current[draggedNode.id] = { x: snappedX, y: snappedY };
    },
    [getNodes, orgChartEdges]
  );

  // ─── Save position on drag end ───
  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, _node, draggedNodes) => {
      // Team-box drag end: persist new positions for every member that
      // moved with the group. The box itself isn't stored — its
      // position is always derived from its members.
      const groupStop = teamDragStart.current;
      if (groupStop && _node.type === 'teamBox') {
        const dx = _node.position.x - groupStop.teamBoxStartPos.x;
        const dy = _node.position.y - groupStop.teamBoxStartPos.y;
        const memberUpdates: { id: string; orgX: number; orgY: number }[] = [];
        for (const [id, start] of groupStop.memberStartPositions.entries()) {
          memberUpdates.push({ id, orgX: start.x + dx, orgY: start.y + dy });
        }
        if (memberUpdates.length > 0) {
          pushUndo({
            kind: 'move',
            updates: memberUpdates.map((u) => {
              const prev = members.find((mm) => mm.id === u.id);
              return {
                id: u.id,
                prevX: prev?.orgX ?? u.orgX,
                prevY: prev?.orgY ?? u.orgY,
                newX: u.orgX,
                newY: u.orgY,
              };
            }),
          });
          onSavePositions(memberUpdates);
        }
        // Tell remote viewers the group drag landed.
        const finalPayload = memberUpdates.map((u) => ({
          id: u.id,
          x: u.orgX,
          y: u.orgY,
          type: 'person' as const,
        }));
        sendOrgChartDrag(finalPayload, false);
        for (const id of groupStop.memberStartPositions.keys()) {
          localDraggingIds.current.delete(id);
        }
        localDraggingIds.current.delete(_node.id);
        teamDragStart.current = null;
        return;
      }

      const shouldSnap = snapActive.current;
      snapActive.current = false;
      setSnapLines({});

      // Apply snapped positions to React Flow state immediately.
      if (shouldSnap) {
        rfSetNodes((nds) =>
          nds.map((n) => {
            const snapped = snapPositions.current[n.id];
            return snapped ? { ...n, position: snapped } : n;
          })
        );
      }

      // Split the dragged nodes by type. Members go through
      // onSavePositions (writes team_members.orgX/Y); junctions go
      // through onUpdateJunction (writes org_chart_junctions.x/y).
      // Team boxes aren't draggable, so they shouldn't appear here.
      const memberUpdates: { id: string; orgX: number; orgY: number }[] = [];
      const junctionUpdates: { id: string; x: number; y: number }[] = [];
      for (const n of draggedNodes) {
        const snapped = shouldSnap ? snapPositions.current[n.id] : undefined;
        const pos = snapped || { x: n.position.x, y: n.position.y };
        if (n.type === 'junction') {
          junctionUpdates.push({
            id: n.id.replace(/^junction-/, ''),
            x: pos.x,
            y: pos.y,
          });
        } else {
          memberUpdates.push({ id: n.id, orgX: pos.x, orgY: pos.y });
        }
      }

      // Final-position broadcast — tells remote viewers the drag is
      // done so they can release the "ghost" overlay. The actual data
      // save fires below via onSavePositions / onUpdateJunction which
      // already broadcasts on its own through the table-save channel.
      const finalPayload = draggedNodes.map((n) => {
        const snapped = shouldSnap ? snapPositions.current[n.id] : undefined;
        const pos = snapped || { x: n.position.x, y: n.position.y };
        return {
          id: n.id,
          x: pos.x,
          y: pos.y,
          type: (n.type === 'junction' ? 'junction' : 'person') as 'person' | 'junction',
        };
      });
      sendOrgChartDrag(finalPayload, false);
      for (const n of draggedNodes) localDraggingIds.current.delete(n.id);

      snapPositions.current = {};
      if (memberUpdates.length > 0) {
        // Snapshot previous positions for undo before persisting.
        pushUndo({
          kind: 'move',
          updates: memberUpdates.map((u) => {
            const prev = members.find((mm) => mm.id === u.id);
            return {
              id: u.id,
              prevX: prev?.orgX ?? u.orgX,
              prevY: prev?.orgY ?? u.orgY,
              newX: u.orgX,
              newY: u.orgY,
            };
          }),
        });
        onSavePositions(memberUpdates);
      }
      for (const u of junctionUpdates) {
        onUpdateJunction(u.id, { x: u.x, y: u.y });
      }
    },
    [members, onSavePositions, onUpdateJunction, rfSetNodes, pushUndo]
  );

  // ─── Multi-select alignment + distribution ───
  // Industry-standard 6 align + 2 distribute operations, applied to
  // the currently-selected PERSON nodes. Team boxes and junctions are
  // excluded — they don't have meaningful "align this to that" semantics.
  // Each op commits in a single setNodes (instant visual), persists via
  // onSavePositions (one batch save), and broadcasts to remote clients
  // so the change is multiplayer-visible.
  type AlignKind = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
  type DistributeKind = 'horizontal' | 'vertical';

  const commitAlignedPositions = useCallback(
    (positions: Array<{ id: string; x: number; y: number }>) => {
      if (positions.length === 0) return;
      rfSetNodes((nds) =>
        nds.map((n) => {
          const p = positions.find((pp) => pp.id === n.id);
          return p ? { ...n, position: { x: p.x, y: p.y } } : n;
        })
      );
      const memberUpdates = positions.map((p) => ({
        id: p.id,
        orgX: p.x,
        orgY: p.y,
      }));
      pushUndo({
        kind: 'move',
        updates: memberUpdates.map((u) => {
          const prev = members.find((mm) => mm.id === u.id);
          return {
            id: u.id,
            prevX: prev?.orgX ?? u.orgX,
            prevY: prev?.orgY ?? u.orgY,
            newX: u.orgX,
            newY: u.orgY,
          };
        }),
      });
      onSavePositions(memberUpdates);
      sendOrgChartDrag(
        positions.map((p) => ({ id: p.id, x: p.x, y: p.y, type: 'person' as const })),
        false
      );
    },
    [rfSetNodes, members, onSavePositions, pushUndo]
  );

  const alignSelection = useCallback(
    (ids: string[], kind: AlignKind) => {
      const sel = getNodes().filter((n) => ids.includes(n.id) && n.type === 'person');
      if (sel.length < 2) return;
      const dims = sel.map((n) => ({
        n,
        w: n.measured?.width ?? NODE_WIDTH,
        h: n.measured?.height ?? NODE_HEIGHT,
      }));
      let positions: Array<{ id: string; x: number; y: number }> = [];
      switch (kind) {
        case 'left': {
          const minX = Math.min(...dims.map((d) => d.n.position.x));
          positions = dims.map((d) => ({ id: d.n.id, x: minX, y: d.n.position.y }));
          break;
        }
        case 'right': {
          const maxRight = Math.max(...dims.map((d) => d.n.position.x + d.w));
          positions = dims.map((d) => ({
            id: d.n.id,
            x: maxRight - d.w,
            y: d.n.position.y,
          }));
          break;
        }
        case 'hcenter': {
          // All horizontal centers land on the same X.
          const avg = dims.reduce((s, d) => s + d.n.position.x + d.w / 2, 0) / dims.length;
          positions = dims.map((d) => ({
            id: d.n.id,
            x: avg - d.w / 2,
            y: d.n.position.y,
          }));
          break;
        }
        case 'top': {
          const minY = Math.min(...dims.map((d) => d.n.position.y));
          positions = dims.map((d) => ({ id: d.n.id, x: d.n.position.x, y: minY }));
          break;
        }
        case 'bottom': {
          const maxBottom = Math.max(...dims.map((d) => d.n.position.y + d.h));
          positions = dims.map((d) => ({
            id: d.n.id,
            x: d.n.position.x,
            y: maxBottom - d.h,
          }));
          break;
        }
        case 'vcenter': {
          // All vertical centers land on the same Y.
          const avg = dims.reduce((s, d) => s + d.n.position.y + d.h / 2, 0) / dims.length;
          positions = dims.map((d) => ({
            id: d.n.id,
            x: d.n.position.x,
            y: avg - d.h / 2,
          }));
          break;
        }
      }
      commitAlignedPositions(positions);
    },
    [getNodes, commitAlignedPositions]
  );

  const distributeSelection = useCallback(
    (ids: string[], kind: DistributeKind) => {
      const sel = getNodes().filter((n) => ids.includes(n.id) && n.type === 'person');
      if (sel.length < 3) return; // distribution needs at least 3
      const dims = sel.map((n) => ({
        n,
        w: n.measured?.width ?? NODE_WIDTH,
        h: n.measured?.height ?? NODE_HEIGHT,
      }));
      let positions: Array<{ id: string; x: number; y: number }> = [];
      if (kind === 'horizontal') {
        const sorted = [...dims].sort((a, b) => a.n.position.x - b.n.position.x);
        const firstLeft = sorted[0].n.position.x;
        const last = sorted[sorted.length - 1];
        const lastRight = last.n.position.x + last.w;
        const totalW = sorted.reduce((s, d) => s + d.w, 0);
        const gap = (lastRight - firstLeft - totalW) / (sorted.length - 1);
        let cursor = firstLeft;
        for (const d of sorted) {
          positions.push({ id: d.n.id, x: cursor, y: d.n.position.y });
          cursor += d.w + gap;
        }
      } else {
        const sorted = [...dims].sort((a, b) => a.n.position.y - b.n.position.y);
        const firstTop = sorted[0].n.position.y;
        const last = sorted[sorted.length - 1];
        const lastBottom = last.n.position.y + last.h;
        const totalH = sorted.reduce((s, d) => s + d.h, 0);
        const gap = (lastBottom - firstTop - totalH) / (sorted.length - 1);
        let cursor = firstTop;
        for (const d of sorted) {
          positions.push({ id: d.n.id, x: d.n.position.x, y: cursor });
          cursor += d.h + gap;
        }
      }
      commitAlignedPositions(positions);
    },
    [getNodes, commitAlignedPositions]
  );

  // ─── Live multiplayer: subscribe to remote drag + cursor events ───
  useEffect(() => {
    const offDrag = onOrgChartDrag((msg) => {
      // Apply remote positions to local React Flow node state. Skip
      // any node the local user is currently dragging — don't fight
      // their input with a stale remote echo.
      rfSetNodes((nds) =>
        nds.map((n) => {
          if (localDraggingIds.current.has(n.id)) return n;
          const remote = msg.nodes.find((r) => r.id === n.id);
          if (!remote) return n;
          return { ...n, position: { x: remote.x, y: remote.y } };
        })
      );
    });
    const offCursor = onOrgChartCursor((msg) => {
      const existing = remoteCursorTtl.current.get(msg.userId);
      if (existing) clearTimeout(existing);
      if (msg.leave) {
        setRemoteCursors((prev) => {
          if (!prev[msg.userId]) return prev;
          const next = { ...prev };
          delete next[msg.userId];
          return next;
        });
        remoteCursorTtl.current.delete(msg.userId);
        return;
      }
      setRemoteCursors((prev) => ({
        ...prev,
        [msg.userId]: { x: msg.x, y: msg.y, userName: msg.userName },
      }));
      // 5-second TTL — drop the cursor if the sender goes silent
      // (closed tab, lost connection, switched away from chart).
      const t = setTimeout(() => {
        setRemoteCursors((prev) => {
          if (!prev[msg.userId]) return prev;
          const next = { ...prev };
          delete next[msg.userId];
          return next;
        });
        remoteCursorTtl.current.delete(msg.userId);
      }, 5000);
      remoteCursorTtl.current.set(msg.userId, t);
    });
    return () => {
      offDrag();
      offCursor();
      sendOrgChartCursorLeave();
      for (const t of remoteCursorTtl.current.values()) clearTimeout(t);
      remoteCursorTtl.current.clear();
    };
  }, [rfSetNodes]);

  // Cursor broadcast — throttled to 20 fps so other viewers see a
  // smooth cursor without us spamming the socket.
  const onCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const now = performance.now();
      if (now - lastCursorSendAt.current < 50) return;
      lastCursorSendAt.current = now;
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      sendOrgChartCursor(pos.x, pos.y);
    },
    [screenToFlowPosition]
  );
  const onCanvasMouseLeave = useCallback(() => {
    sendOrgChartCursorLeave();
  }, []);

  // ─── Visio-style connect: drag handle to handle → create an edge ───
  // Cycle detection walks the org_chart_edges graph (not reports_to) so
  // any visible parent chain blocks a back-pointing connection.
  const isValidConnection: IsValidConnection = useCallback(
    (connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target)
        return false;
      // Build a parents-of map keyed by the React Flow node id space so
      // team boxes (prefixed 'team-box-') and members share one graph
      // for cycle detection.
      const keyOf = (e: OrgChartEdge, side: 'src' | 'tgt'): string => {
        const id = side === 'src' ? e.sourceMemberId : e.targetMemberId;
        const ty = side === 'src' ? e.sourceType : e.targetType;
        if (ty === 'team') return `team-box-${id}`;
        if (ty === 'junction') return `junction-${id}`;
        return id;
      };
      const parentsOf = new Map<string, string[]>();
      for (const e of orgChartEdges) {
        const t = keyOf(e, 'tgt');
        const s = keyOf(e, 'src');
        const list = parentsOf.get(t) || [];
        list.push(s);
        parentsOf.set(t, list);
      }
      const visited = new Set<string>();
      const stack: string[] = [connection.source];
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (visited.has(cur)) continue;
        visited.add(cur);
        if (cur === connection.target) return false;
        for (const p of parentsOf.get(cur) || []) stack.push(p);
      }
      return true;
    },
    [orgChartEdges]
  );

  const onConnect = useCallback(
    async (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      const sourceHandle =
        (connection.sourceHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined;
      const targetHandle =
        (connection.targetHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined;
      // Team-box node ids carry a 'team-box-' prefix; strip it so the
      // persisted row stores the bare team id and flag with sourceType
      // / targetType. Member ids pass through.
      const parseEndpoint = (id: string): { id: string; type: 'member' | 'team' | 'junction' } => {
        if (id.startsWith('team-box-')) return { id: id.slice('team-box-'.length), type: 'team' };
        if (id.startsWith('junction-'))
          return { id: id.slice('junction-'.length), type: 'junction' };
        return { id, type: 'member' };
      };
      const src = parseEndpoint(connection.source!);
      const tgt = parseEndpoint(connection.target!);
      // Disallow team→team self-loops (no useful semantics there).
      if (src.type === 'team' && tgt.type === 'team' && src.id === tgt.id) return;
      const existing = orgChartEdges.find(
        (e) =>
          e.sourceMemberId === src.id &&
          e.targetMemberId === tgt.id &&
          (e.sourceType || 'member') === src.type &&
          (e.targetType || 'member') === tgt.type
      );
      if (existing) {
        await onUpdateEdge(existing.id, { sourceHandle, targetHandle });
        return;
      }
      const now = new Date().toISOString();
      const newEdge: OrgChartEdge = {
        id: crypto.randomUUID(),
        sourceMemberId: src.id,
        targetMemberId: tgt.id,
        sourceType: src.type,
        targetType: tgt.type,
        style: 'solid',
        sourceHandle,
        targetHandle,
        createdAt: now,
        updatedAt: now,
      };
      await onCreateEdge(newEdge);
      pushUndo({ kind: 'edge-create', edge: newEdge });
    },
    [orgChartEdges, onCreateEdge, onUpdateEdge, pushUndo]
  );

  // Wrap deletion so the undo stack remembers the full row.
  const deleteEdgeWithUndo = useCallback(
    async (edgeId: string) => {
      const edge = orgChartEdges.find((e) => e.id === edgeId);
      await onDeleteEdge(edgeId);
      if (edge) pushUndo({ kind: 'edge-delete', edge });
    },
    [orgChartEdges, onDeleteEdge, pushUndo]
  );

  // ─── Execute one undo / redo step ─────────────────────────────────
  // We pop from one stack, run the inverse, push the inverse to the
  // OTHER stack — same pattern as every text editor. The state pushed
  // is the inverse of the popped command so a redo undoes the undo.
  const applyCmd = useCallback(
    async (cmd: UndoCmd, isRedo: boolean): Promise<UndoCmd> => {
      switch (cmd.kind) {
        case 'move': {
          // Undo = restore prev; Redo = re-apply new.
          const updates = cmd.updates.map((u) =>
            isRedo
              ? { id: u.id, orgX: u.newX, orgY: u.newY }
              : { id: u.id, orgX: u.prevX, orgY: u.prevY }
          );
          await onSavePositions(updates);
          return {
            kind: 'move',
            updates: cmd.updates.map((u) => ({
              id: u.id,
              prevX: isRedo ? u.prevX : u.newX,
              prevY: isRedo ? u.prevY : u.newY,
              newX: isRedo ? u.newX : u.prevX,
              newY: isRedo ? u.newY : u.prevX,
            })),
          };
        }
        case 'edge-create': {
          if (isRedo) {
            await onCreateEdge(cmd.edge);
            return { kind: 'edge-create', edge: cmd.edge };
          }
          await onDeleteEdge(cmd.edge.id);
          return { kind: 'edge-delete', edge: cmd.edge };
        }
        case 'edge-delete': {
          if (isRedo) {
            await onDeleteEdge(cmd.edge.id);
            return { kind: 'edge-delete', edge: cmd.edge };
          }
          await onCreateEdge(cmd.edge);
          return { kind: 'edge-create', edge: cmd.edge };
        }
      }
    },
    [onSavePositions, onCreateEdge, onDeleteEdge]
  );
  const doUndo = useCallback(async () => {
    const cmd = undoStack.current.pop();
    if (!cmd) return;
    const inverse = await applyCmd(cmd, false);
    redoStack.current.push(inverse);
  }, [applyCmd]);
  const doRedo = useCallback(async () => {
    const cmd = redoStack.current.pop();
    if (!cmd) return;
    const inverse = await applyCmd(cmd, true);
    undoStack.current.push(inverse);
  }, [applyCmd]);

  // ─── Keyboard shortcuts ─────────────────────────────────────────
  // Ignored while an input/textarea has focus so typing in the label
  // editor doesn't trigger delete-the-selection. Delete on selected
  // edge(s); arrow nudge on selected node(s) (Shift = 5x). Ctrl/Cmd+Z
  // undo; Ctrl+Y or Ctrl+Shift+Z redo.
  useEffect(() => {
    if (!canEdit) return;
    const NUDGE = 8;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        doUndo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))
      ) {
        e.preventDefault();
        doRedo();
        return;
      }
      const selectedNodes = getNodes().filter((n) => n.selected);
      const selectedEdgeIds = (edges as Edge[]).filter((ed) => ed.selected).map((ed) => ed.id);
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedEdgeIds.length > 0) {
          e.preventDefault();
          for (const id of selectedEdgeIds) deleteEdgeWithUndo(id);
        }
        return;
      }
      if (
        selectedNodes.length > 0 &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
      ) {
        e.preventDefault();
        const step = e.shiftKey ? NUDGE * 5 : NUDGE;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        const updates = selectedNodes.map((n) => ({
          id: n.id,
          orgX: n.position.x + dx,
          orgY: n.position.y + dy,
        }));
        pushUndo({
          kind: 'move',
          updates: updates.map((u) => {
            const prev = members.find((mm) => mm.id === u.id);
            return {
              id: u.id,
              prevX: prev?.orgX ?? u.orgX - dx,
              prevY: prev?.orgY ?? u.orgY - dy,
              newX: u.orgX,
              newY: u.orgY,
            };
          }),
        });
        rfSetNodes((nds) =>
          nds.map((n) =>
            n.selected ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } } : n
          )
        );
        onSavePositions(updates);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    canEdit,
    doUndo,
    doRedo,
    deleteEdgeWithUndo,
    edges,
    getNodes,
    rfSetNodes,
    onSavePositions,
    members,
    pushUndo,
  ]);

  // ─── Reconnect: drag an existing edge's endpoint to a different node
  // or handle. React Flow exposes this via the "reconnectable" anchor
  // shown at each end when an edge is selected. We patch the existing
  // edge in place so the user keeps the label, color, and waypoints
  // they set — no delete + recreate. */
  const onReconnect = useCallback(
    async (oldEdge: Edge, newConnection: Connection) => {
      const data = (oldEdge.data || {}) as OrgEdgeData;
      const edgeId = data.edgeId || oldEdge.id;
      const parseEndpoint = (id: string): { id: string; type: 'member' | 'team' | 'junction' } => {
        if (id.startsWith('team-box-')) return { id: id.slice('team-box-'.length), type: 'team' };
        if (id.startsWith('junction-'))
          return { id: id.slice('junction-'.length), type: 'junction' };
        return { id, type: 'member' };
      };
      const src = parseEndpoint(newConnection.source!);
      const tgt = parseEndpoint(newConnection.target!);
      await onUpdateEdge(edgeId, {
        sourceMemberId: src.id,
        targetMemberId: tgt.id,
        sourceType: src.type,
        targetType: tgt.type,
        sourceHandle:
          (newConnection.sourceHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined,
        targetHandle:
          (newConnection.targetHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined,
      });
    },
    [onUpdateEdge]
  );

  // ─── Inline edge context menu state ───
  // No more window.confirm — right-click opens this menu near the cursor.
  // Items: pick source/target side, pick style, recolor, edit label,
  // delete. All changes hit onUpdateEdge / onDeleteEdge synchronously.
  const [edgeMenu, setEdgeMenu] = useState<{
    edgeId: string;
    x: number;
    y: number;
  } | null>(null);

  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    setEdgeMenu({ edgeId: edge.id, x: event.clientX, y: event.clientY });
  }, []);

  // (Old `addControlPointAt` helper removed in v2.13.12 — superseded
  //  by the junction-node model. To bend a line, add a Junction.)

  // Double-click an edge to inline-edit its label.
  const [labelEditor, setLabelEditor] = useState<{
    edgeId: string;
    value: string;
    x: number;
    y: number;
  } | null>(null);
  const onEdgeDoubleClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (!canEdit) return;
      event.preventDefault();
      event.stopPropagation();
      const d = (edge.data || {}) as OrgEdgeData;
      setLabelEditor({
        edgeId: edge.id,
        value: d.label || '',
        x: event.clientX,
        y: event.clientY,
      });
    },
    [canEdit]
  );

  // ─── Context menu ───
  const onNodeContextMenu: NodeMouseHandler = useCallback(
    (event, node) => {
      event.preventDefault();
      // Junction nodes get a direct delete (no menu) — they're meant to
      // be lightweight. Cascade-removes any edges referencing the
      // junction so the chart doesn't leave dangling lines.
      if (node.type === 'junction') {
        const junctionId = node.id.replace(/^junction-/, '');
        onDeleteJunction(junctionId);
        return;
      }
      // Team boxes have no per-node menu items beyond what the team
      // settings already offer; suppress the chart-level ctx menu for
      // them.
      if (node.type === 'teamBox') return;
      setCtxMenu({ memberId: node.id, x: event.clientX, y: event.clientY });
    },
    [onDeleteJunction]
  );

  // ─── Double-click to edit ───
  const onNodeDoubleClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      const m = members.find((x) => x.id === node.id);
      if (m) onMemberClick(m);
    },
    [members, onMemberClick]
  );

  // ─── Drop from palette onto React Flow canvas ───
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!canEdit) return;
      const raw = event.dataTransfer.getData('application/json');
      if (!raw) return;
      let payload: { type: string; id: string };
      try {
        payload = JSON.parse(raw);
      } catch {
        return;
      }

      if (payload.type === 'member') {
        const m = members.find((x) => x.id === payload.id);
        if (!m) return;
        if (!onChart.has(m.id)) {
          const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
          onAddToChart(m.id, flowPos.x, flowPos.y);
        }
      } else if (payload.type === 'team') {
        // Find which node the team token was dropped on
        const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        const allNodes = getNodes();
        const targetNode = allNodes.find((n) => {
          const nw = n.measured?.width ?? NODE_WIDTH;
          const nh = n.measured?.height ?? NODE_HEIGHT;
          return (
            flowPos.x >= n.position.x &&
            flowPos.x <= n.position.x + nw &&
            flowPos.y >= n.position.y &&
            flowPos.y <= n.position.y + nh
          );
        });
        if (targetNode) {
          // If target is selected and multiple are selected, assign team to all selected
          const selected = allNodes.filter((n) => n.selected);
          const targets =
            selected.length > 1 && selected.some((n) => n.id === targetNode.id)
              ? selected.map((n) => n.id)
              : [targetNode.id];
          for (const id of targets) onTeamAssign(id, payload.id);
        }
      }
    },
    [
      canEdit,
      members,
      onChart,
      onReportsToChange,
      onTeamAssign,
      screenToFlowPosition,
      getNodes,
      onAddToChart,
    ]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // ─── Palette filter ───
  const sq = search.toLowerCase();
  // Only show members NOT already on the chart in the palette; teams always shown (for assigning)
  const offChart = members.filter((m) => !onChart.has(m.id) && m.active);
  const fm = sq
    ? offChart.filter(
        (m) => m.name.toLowerCase().includes(sq) || (m.email || '').toLowerCase().includes(sq)
      )
    : offChart;
  const ft = sq
    ? teams.filter(
        (t) => t.name.toLowerCase().includes(sq) || (t.shortCode || '').toLowerCase().includes(sq)
      )
    : teams;

  // ─── Empty state ───
  if (members.length === 0) {
    return (
      <EmptyState
        title="No team members"
        subtitle="Add team members in the Members tab to get started."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      {/* ═══ Palette bar ═══
          The palette is the drag source for adding members/teams onto the
          chart. View-only users can't drop, so showing them an empty
          drag-source is noise — hide the whole bar and give them the
          chart at full height. */}
      {canEdit && (
        <div
          ref={paletteRef}
          style={{
            background: 'var(--sa)',
            borderRadius: 10,
            border: '1px solid var(--bd)',
            padding: '8px 10px',
            display: 'flex',
            gap: 10,
            marginBottom: 8,
            flexShrink: 0,
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <div style={{ position: 'relative' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  width: 120,
                  padding: '5px 8px',
                  paddingRight: search ? 22 : 8,
                  borderRadius: 5,
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 13,
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  title="Clear search"
                  style={{
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--tm)',
                    cursor: 'pointer',
                    lineHeight: 1,
                    padding: 4,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--bd)', flexShrink: 0 }} />
          {/* Two rows */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {/* People row */}
            <div
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                overflow: 'auto',
                padding: '2px 0',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--tm)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  flexShrink: 0,
                }}
              >
                People
              </span>
              <div style={{ width: 1, height: 20, background: 'var(--bd)', flexShrink: 0 }} />
              {fm.length > 0 ? (
                fm.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      flexShrink: 0,
                    }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({ type: 'member', id: m.id })
                      );
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  >
                    <MemberToken
                      member={m}
                      color={getMemberTeamColor(m.id)}
                      size={32}
                      style={{ cursor: 'grab' }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--tm)',
                        maxWidth: 48,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                    >
                      {m.name.split(' ')[0]}
                    </span>
                  </div>
                ))
              ) : (
                <span style={{ fontSize: 10, color: 'var(--tm)', fontStyle: 'italic' }}>
                  No matches
                </span>
              )}
            </div>
            {/* Teams row */}
            <div
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                overflow: 'auto',
                padding: '2px 0',
                borderTop: '1px solid var(--bd)',
                paddingTop: 4,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--tm)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  flexShrink: 0,
                }}
              >
                Teams
              </span>
              <div style={{ width: 1, height: 20, background: 'var(--bd)', flexShrink: 0 }} />
              {ft.length > 0 ? (
                ft.map((t) => (
                  <TeamToken
                    key={t.id}
                    team={t}
                    size={28}
                    style={{ cursor: 'grab' }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({ type: 'team', id: t.id })
                      );
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  />
                ))
              ) : (
                <span style={{ fontSize: 10, color: 'var(--tm)', fontStyle: 'italic' }}>
                  {teams.length === 0 ? 'No teams yet' : 'No matches'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Toolbar: edge style picker + auto-arrange ═══
          Visible to everyone (line-style is a per-device view preference),
          but auto-arrange (which mutates positions for everyone) is hidden
          for view-only users. A subtle "View only" badge replaces the
          shortcut hint so users understand why dragging does nothing. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 4px',
          marginBottom: 6,
          flexShrink: 0,
        }}
      >
        {/* Edge style segmented control */}
        <div
          role="radiogroup"
          aria-label="Org chart line style"
          style={{
            display: 'inline-flex',
            border: '1px solid var(--bd)',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--sf)',
          }}
        >
          {(
            [
              {
                value: 'smoothstep',
                label: 'Rounded',
                hint: 'Right-angle lines with rounded corners',
              },
              { value: 'step', label: 'Square', hint: 'Sharp right-angle lines' },
              { value: 'straight', label: 'Straight', hint: 'Direct diagonal lines' },
              { value: 'bezier', label: 'Curved', hint: 'Smooth bezier curves' },
            ] as { value: OrgEdgeRouting; label: string; hint: string }[]
          ).map((opt) => {
            const active = routing === opt.value;
            return (
              <button
                key={opt.value}
                role="radio"
                aria-checked={active}
                title={opt.hint}
                onClick={() => setRouting(opt.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: 11,
                  fontWeight: active ? 600 : 500,
                  background: active ? 'var(--ac)' : 'transparent',
                  color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                  border: 'none',
                  borderRight: '1px solid var(--bd)',
                  cursor: 'pointer',
                  transition: 'background 120ms ease, color 120ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Auto-arrange: clears positions/waypoints/handles, re-runs dagre.
            Hidden for view-only users — auto-arrange mutates shared state
            (every user sees the chart re-laid-out) so it's edit-only. */}
        {canEdit && (
          <button
            onClick={() => {
              if (
                !window.confirm(
                  'Auto-arrange will reset all node positions and clear any custom line routing. Continue?'
                )
              )
                return;
              // 1) Clear waypoints + handle overrides on every edge so
              //    dagre's chosen routing isn't fought by stale custom
              //    routing.
              for (const e of orgChartEdges) {
                onUpdateEdge(e.id, {
                  waypoints: undefined,
                  sourceHandle: undefined,
                  targetHandle: undefined,
                });
              }
              // 2) Run dagre over the current node/edge set.
              const layouted = getLayoutedElements(nodes, edges);
              // 3) Animate to new positions — React Flow's transition CSS
              //    handles this when we assign a transition class, but
              //    React Flow doesn't drive nodes via CSS, so we
              //    interpolate ourselves over ~400ms with rAF.
              const start = performance.now();
              const startPos = new Map(nodes.map((n) => [n.id, { ...n.position }]));
              const endPos = new Map(layouted.map((n) => [n.id, { ...n.position }]));
              const tick = (now: number) => {
                const t = Math.min(1, (now - start) / 400);
                // ease-in-out cubic
                const k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
                setNodes((prev) =>
                  prev.map((n) => {
                    const a = startPos.get(n.id);
                    const b = endPos.get(n.id);
                    if (!a || !b) return n;
                    return {
                      ...n,
                      position: { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k },
                    };
                  })
                );
                if (t < 1) requestAnimationFrame(tick);
                else
                  onSavePositions(
                    layouted.map((n) => ({ id: n.id, orgX: n.position.x, orgY: n.position.y }))
                  );
              };
              requestAnimationFrame(tick);
              // 4) Rebuild edges so handle pairs auto-pick from new geometry
              setEdges((prev) =>
                prev.map((e) => ({
                  ...e,
                  sourceHandle: undefined,
                  targetHandle: undefined,
                  data: { ...(e.data || {}), waypoints: [] } as OrgEdgeData,
                }))
              );
            }}
            title="Reset positions and let the auto-layout arrange the chart top-down"
            style={{
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 500,
              background: 'var(--sf)',
              color: 'var(--tx)',
              border: '1px solid var(--bd)',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Auto-arrange
          </button>
        )}

        {/* Snap toggle — flip alignment snap on/off for node drags
            and control-point drags. Default on. Persisted per-device
            in localStorage. */}
        <button
          onClick={() => setSnapEnabled(!snapEnabled)}
          title={snapEnabled ? 'Turn snap-to-align off' : 'Turn snap-to-align on'}
          aria-pressed={snapEnabled}
          style={{
            padding: '6px 12px',
            fontSize: 11,
            fontWeight: snapEnabled ? 600 : 500,
            background: snapEnabled ? 'var(--ac)' : 'var(--sf)',
            color: snapEnabled ? 'var(--color-text-inverse)' : 'var(--tx)',
            border: '1px solid var(--bd)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 120ms ease, color 120ms ease',
          }}
        >
          Snap {snapEnabled ? 'on' : 'off'}
        </button>

        {/* + Junction — drop a new junction node at the viewport
            center. Junctions are small connectable hubs you use to
            consolidate "one-to-many" lines: drag the dot where you
            want it, then connect lines to it. */}
        {canEdit && (
          <button
            onClick={async () => {
              const center = screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
              });
              await onCreateJunction(center.x, center.y);
            }}
            title="Add a junction node — a connection hub for consolidating one-to-many lines"
            style={{
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 500,
              background: 'var(--sf)',
              color: 'var(--tx)',
              border: '1px solid var(--bd)',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            + Junction
          </button>
        )}

        {/* Team-box dropdown — per-team visibility. Each team in the
            checklist can be toggled independently so a user can show
            just the Managing Principals box without bringing in every
            other team's backdrop. Closes on outside-click via the
            React Flow pane handler. */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setTeamBoxMenuOpen((v) => !v)}
            title="Pick which team backdrops to show"
            aria-haspopup="menu"
            aria-expanded={teamBoxMenuOpen}
            style={{
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: anyTeamBoxVisible ? 600 : 500,
              background: anyTeamBoxVisible ? 'var(--ac)' : 'var(--sf)',
              color: anyTeamBoxVisible ? 'var(--color-text-inverse)' : 'var(--tx)',
              border: '1px solid var(--bd)',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'background 120ms ease, color 120ms ease',
            }}
          >
            Team boxes
            {anyTeamBoxVisible && (
              <span style={{ marginLeft: 6, opacity: 0.85 }}>
                ({Object.values(visibleTeamIds).filter(Boolean).length})
              </span>
            )}
            <span style={{ marginLeft: 6, fontSize: 10 }}>▾</span>
          </button>
          {teamBoxMenuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 4,
                zIndex: 240,
                background: 'var(--sa)',
                border: '1px solid var(--bd)',
                borderRadius: 10,
                boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                padding: 6,
                minWidth: 200,
                maxHeight: 320,
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  padding: '4px 6px',
                  borderBottom: '1px solid var(--bd)',
                  marginBottom: 4,
                }}
              >
                <button
                  onClick={() => setAllTeamsVisible(true)}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: 10,
                    background: 'var(--sf)',
                    border: '1px solid var(--bd)',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Show all
                </button>
                <button
                  onClick={() => setAllTeamsVisible(false)}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: 10,
                    background: 'var(--sf)',
                    border: '1px solid var(--bd)',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Hide all
                </button>
              </div>
              {teams.length === 0 && (
                <div style={{ padding: '8px 6px', fontSize: 11, color: 'var(--tm)' }}>
                  No teams yet
                </div>
              )}
              {teams.map((team) => {
                const checked = !!visibleTeamIds[team.id];
                return (
                  <label
                    key={team.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 8px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.background = 'var(--sf)')
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.background = 'transparent')
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setTeamBoxVisible(team.id, e.target.checked)}
                    />
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: team.color || 'var(--ac)',
                        flexShrink: 0,
                      }}
                    />
                    <span>{team.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />
        {canEdit ? (
          <span style={{ fontSize: 10, color: 'var(--tm)' }}>
            Scroll to zoom &middot; Drag any side dot to connect &middot; <b>+ Junction</b> drops a
            connection hub — drag it to position, connect lines to it &middot; Right-click a
            junction to delete &middot; Right-click a line for style / label / color &middot; Ctrl+Z
            undo
          </span>
        ) : (
          <span
            title="You don't have permission to edit team members"
            style={{
              fontSize: 10,
              color: 'var(--tm)',
              padding: '2px 8px',
              border: '1px solid var(--bd)',
              borderRadius: 999,
              background: 'var(--sf)',
            }}
          >
            View only &middot; scroll to zoom &middot; click a node for details
          </span>
        )}
      </div>

      {/* ═══ React Flow Canvas ═══ */}
      <div
        style={{
          flex: 1,
          borderRadius: 10,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
          position: 'relative',
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onMouseMove={onCanvasMouseMove}
        onMouseLeave={onCanvasMouseLeave}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStart={canEdit ? onNodeDragStart : undefined}
          onNodeDrag={canEdit ? onNodeDrag : undefined}
          onNodeDragStop={canEdit ? onNodeDragStop : undefined}
          onConnect={canEdit ? onConnect : undefined}
          onReconnect={canEdit ? onReconnect : undefined}
          isValidConnection={canEdit ? isValidConnection : () => false}
          connectionMode={ConnectionMode.Loose}
          nodesDraggable={canEdit}
          nodesConnectable={canEdit}
          edgesReconnectable={canEdit}
          onNodeContextMenu={canEdit ? onNodeContextMenu : undefined}
          onEdgeContextMenu={canEdit ? onEdgeContextMenu : undefined}
          onNodeDoubleClick={onNodeDoubleClick}
          onEdgeDoubleClick={canEdit ? onEdgeDoubleClick : undefined}
          onPaneClick={() => {
            setCtxMenu(null);
            setEdgeMenu(null);
            setTeamBoxMenuOpen(false);
          }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          panOnDrag
          selectionKeyCode="Shift"
          multiSelectionKeyCode="Shift"
          minZoom={0.05}
          maxZoom={3}
          defaultEdgeOptions={{
            type: 'org',
            style: { stroke: 'var(--bd)', strokeWidth: 1.5 },
          }}
          style={{ background: 'var(--sf)' }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="var(--bd)" />
          <Controls
            showInteractive={false}
            style={{ borderRadius: 8, border: '1px solid var(--bd)', boxShadow: 'none' }}
          />
          <MiniMap
            nodeColor={(n: Node) => {
              const data = n.data as PersonNodeData;
              return data?.teamColor || 'var(--ac)';
            }}
            maskColor="rgba(0,0,0,0.08)"
            style={{ borderRadius: 8, border: '1px solid var(--bd)' }}
          />
          {/* Snap alignment guide lines rendered in flow coordinate space */}
          <SnapGuideLines hY={snapLines.hY} vX={snapLines.vX} />
          {/* Live cursors of other firm members on this chart */}
          <RemoteCursors cursors={remoteCursors} />
        </ReactFlow>
      </div>

      {/* ═══ Edge Context Menu ═══
          Inline replacement for window.confirm. Renders right at the
          cursor with the four side pickers (Top/Right/Bottom/Left),
          three line styles, a color row, edit-label, and delete. Every
          change persists immediately and the menu stays open so the
          user can stack changes before dismissing. */}
      {edgeMenu &&
        (() => {
          const edge = orgChartEdges.find((e) => e.id === edgeMenu.edgeId);
          if (!edge) return null;
          const sides: ('top' | 'right' | 'bottom' | 'left')[] = ['top', 'right', 'bottom', 'left'];
          const styles: { value: 'solid' | 'dashed' | 'dotted'; label: string }[] = [
            { value: 'solid', label: 'Solid' },
            { value: 'dashed', label: 'Dashed' },
            { value: 'dotted', label: 'Dotted' },
          ];
          const colors = ORG_EDGE_COLORS;
          const sideIcon = (s: string) =>
            (({ top: '↑', right: '→', bottom: '↓', left: '←' }) as Record<string, string>)[s];
          return (
            <div
              role="menu"
              style={{
                position: 'fixed',
                left: edgeMenu.x,
                top: edgeMenu.y,
                zIndex: 250,
                background: 'var(--sa)',
                border: '1px solid var(--bd)',
                borderRadius: 10,
                boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                padding: 8,
                minWidth: 220,
                fontSize: 13,
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setEdgeMenu(null);
              }}
            >
              {/* Source side */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ color: 'var(--tm)', fontSize: 10, width: 50 }}>From side</span>
                {sides.map((s) => {
                  const active = (edge.sourceHandle || '') === s;
                  return (
                    <button
                      key={s}
                      title={s}
                      onClick={() => onUpdateEdge(edge.id, { sourceHandle: s })}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: active ? 'var(--ac)' : 'var(--sf)',
                        color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                        cursor: 'pointer',
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >
                      {sideIcon(s)}
                    </button>
                  );
                })}
              </div>
              {/* Target side */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ color: 'var(--tm)', fontSize: 10, width: 50 }}>To side</span>
                {sides.map((s) => {
                  const active = (edge.targetHandle || '') === s;
                  return (
                    <button
                      key={s}
                      title={s}
                      onClick={() => onUpdateEdge(edge.id, { targetHandle: s })}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: active ? 'var(--ac)' : 'var(--sf)',
                        color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                        cursor: 'pointer',
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >
                      {sideIcon(s)}
                    </button>
                  );
                })}
              </div>
              {/* Style */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ color: 'var(--tm)', fontSize: 10, width: 50 }}>Style</span>
                {styles.map((s) => {
                  const active = (edge.style || 'solid') === s.value;
                  return (
                    <button
                      key={s.value}
                      onClick={() => onUpdateEdge(edge.id, { style: s.value })}
                      style={{
                        padding: '3px 8px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: active ? 'var(--ac)' : 'var(--sf)',
                        color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                        cursor: 'pointer',
                        fontSize: 11,
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
              {/* Color */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ color: 'var(--tm)', fontSize: 10, width: 50 }}>Color</span>
                {colors.map((c) => {
                  const active = (edge.color || 'var(--bd)') === c;
                  return (
                    <button
                      key={c}
                      onClick={() =>
                        onUpdateEdge(edge.id, { color: c === 'var(--bd)' ? undefined : c })
                      }
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        border: active ? '2px solid var(--tx)' : '1px solid var(--bd)',
                        background: c,
                        cursor: 'pointer',
                      }}
                      title={c}
                      aria-label={`Line color ${c}`}
                    />
                  );
                })}
              </div>
              {/* Label edit + delete */}
              <div
                style={{ display: 'flex', gap: 6, borderTop: '1px solid var(--bd)', paddingTop: 8 }}
              >
                <button
                  onClick={() => {
                    setLabelEditor({
                      edgeId: edge.id,
                      value: edge.label || '',
                      x: edgeMenu.x,
                      y: edgeMenu.y,
                    });
                    setEdgeMenu(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                    background: 'var(--sf)',
                    color: 'var(--tx)',
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                >
                  {edge.label ? 'Edit label' : 'Add label'}
                </button>
                {(edge.waypoints?.length ?? 0) > 0 && (
                  <button
                    onClick={async () => {
                      setEdgeMenu(null);
                      // Empty array explicitly clears the field — the
                      // server's prepareForJsonb handles arrays
                      // correctly and pg stores '[]' jsonb, which
                      // round-trips back as []. Using `undefined`
                      // here used to silently drop in the spread on
                      // the client side; the explicit [] survives.
                      await onUpdateEdge(edge.id, { waypoints: [] });
                    }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: '1px solid var(--bd)',
                      background: 'var(--sf)',
                      color: 'var(--tx)',
                      cursor: 'pointer',
                      fontSize: 11,
                    }}
                    title="Clear all control points and let the line auto-route"
                  >
                    Reset routing
                  </button>
                )}
                <button
                  onClick={async () => {
                    setEdgeMenu(null);
                    await deleteEdgeWithUndo(edge.id);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                    background: 'color-mix(in srgb, var(--dg) 12%, transparent)',
                    color: 'var(--dg)',
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })()}

      {/* ═══ Edge Label Editor ═══
          Tiny inline input that appears at the click position. Enter to
          save, Escape to cancel. No modal, no extra screen real estate. */}
      {labelEditor &&
        (() => {
          const cancel = () => setLabelEditor(null);
          const commit = async () => {
            const val = labelEditor.value.trim();
            await onUpdateEdge(labelEditor.edgeId, { label: val || undefined });
            setLabelEditor(null);
          };
          return (
            <div
              style={{
                position: 'fixed',
                left: labelEditor.x,
                top: labelEditor.y,
                zIndex: 260,
                background: 'var(--sa)',
                border: '1px solid var(--ac)',
                borderRadius: 8,
                padding: 6,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
            >
              <input
                autoFocus
                value={labelEditor.value}
                placeholder="Line label…"
                onChange={(e) => setLabelEditor({ ...labelEditor, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') cancel();
                }}
                onBlur={commit}
                style={{
                  width: 180,
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--bd)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
            </div>
          );
        })()}

      {/* ═══ Context Menu ═══ */}
      {ctxMenu &&
        (() => {
          const m = members.find((x) => x.id === ctxMenu.memberId);
          if (!m) return null;

          // Check if multiple nodes are selected (for batch operations)
          const selectedNodes = getNodes().filter((n) => n.selected);
          const selectedIds =
            selectedNodes.length > 1 && selectedNodes.some((n) => n.id === m.id)
              ? selectedNodes.map((n) => n.id)
              : [m.id];
          const isMulti = selectedIds.length > 1;
          const multiLabel = isMulti ? ` (${selectedIds.length} selected)` : '';

          const mTeams = getMemberTeams(m.id);
          const mTeamAssigns = teamAssignments.filter((a) => a.teamMemberId === m.id);
          const items: {
            label: string;
            action: () => void;
            danger?: boolean;
            separator?: boolean;
          }[] = [];

          // Detach — works for all selected
          const detachable = selectedIds.filter((id) => {
            const mm = members.find((x) => x.id === id);
            return mm?.reportsTo && mm.reportsTo !== '__root__';
          });
          if (detachable.length > 0) {
            items.push({
              label: `Detach${multiLabel}`,
              action: () => {
                for (const id of detachable) onReportsToChange(id, '__root__');
                // Instantly remove edges targeting detached nodes
                setEdges((prev) => prev.filter((e) => !detachable.includes(e.target)));
              },
            });
          }

          // Team assignments (single-node only for role toggle)
          if (!isMulti) {
            for (const t of mTeams) {
              const assign = mTeamAssigns.find((a) => a.teamId === t.id);
              const isLeader = assign?.teamRole === 'leader';
              items.push({
                label: `${t.name}: ${isLeader ? 'Demote to Member' : 'Promote to Leader'}`,
                action: () => onToggleTeamRole(m.id, t.id),
              });
            }
          }

          // Remove from team — batch for all selected
          const allTeamIds = new Set(
            selectedIds.flatMap((id) =>
              teamAssignments.filter((a) => a.teamMemberId === id).map((a) => a.teamId)
            )
          );
          for (const tid of allTeamIds) {
            const t = teams.find((x) => x.id === tid);
            if (t) {
              items.push({
                label: `Remove from ${t.name}${multiLabel}`,
                action: () => {
                  for (const id of selectedIds) onTeamRemove(id, tid);
                },
              });
            }
          }

          // Alignment + distribution — only meaningful with 2+ persons.
          if (isMulti) {
            if (items.length > 0) items.push({ label: '', action: () => {}, separator: true });
            items.push({
              label: 'Align left',
              action: () => alignSelection(selectedIds, 'left'),
            });
            items.push({
              label: 'Align horizontal center',
              action: () => alignSelection(selectedIds, 'hcenter'),
            });
            items.push({
              label: 'Align right',
              action: () => alignSelection(selectedIds, 'right'),
            });
            items.push({
              label: 'Align top',
              action: () => alignSelection(selectedIds, 'top'),
            });
            items.push({
              label: 'Align vertical center',
              action: () => alignSelection(selectedIds, 'vcenter'),
            });
            items.push({
              label: 'Align bottom',
              action: () => alignSelection(selectedIds, 'bottom'),
            });
            if (selectedIds.length >= 3) {
              items.push({
                label: 'Distribute horizontally',
                action: () => distributeSelection(selectedIds, 'horizontal'),
              });
              items.push({
                label: 'Distribute vertically',
                action: () => distributeSelection(selectedIds, 'vertical'),
              });
            }
          }

          if (items.length > 0) items.push({ label: '', action: () => {}, separator: true });
          const removable = selectedIds.filter((id) => {
            const mm = members.find((x) => x.id === id);
            return mm?.reportsTo || getMemberTeams(id).length > 0;
          });
          if (removable.length > 0) {
            items.push({
              label: `Remove from chart${multiLabel}`,
              action: () => {
                for (const id of removable) onRemoveFromChart(id);
              },
            });
          }
          if (!isMulti) {
            items.push({ label: 'Edit member...', action: () => onMemberClick(m) });
          }
          items.push({
            label: `Delete${multiLabel}`,
            action: () => {
              for (const id of selectedIds) onDeleteMember(id);
            },
            danger: true,
          });

          return (
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 200 }}
              onClick={() => setCtxMenu(null)}
              onContextMenu={(e) => {
                e.preventDefault();
                setCtxMenu(null);
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: ctxMenu.x,
                  top: ctxMenu.y,
                  background: 'var(--sf)',
                  border: '1px solid var(--bd)',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,.18)',
                  padding: '4px 0',
                  minWidth: 180,
                  zIndex: 201,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {items.map((item, i) =>
                  item.separator ? (
                    <div key={i} style={{ height: 1, background: 'var(--bd)', margin: '3px 0' }} />
                  ) : (
                    <button
                      key={i}
                      onClick={() => {
                        setCtxMenu(null);
                        item.action();
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '7px 14px',
                        fontSize: 13,
                        fontWeight: 500,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: item.danger ? 'var(--dg)' : 'var(--tx)',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      {item.label}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })()}
    </div>
  );
}

// ─── Teams Tab ───

interface TeamsTabProps {
  teams: Team[];
  members: TeamMember[];
  teamAssignments: TeamAssignment[];
  memberName: (id?: string) => string;
  getTeamMemberAssignments: (teamId: string) => TeamAssignment[];
  getTeamLeader: (teamId: string) => string;
  onEdit: (t: Team) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onCellEdit?: (team: Team, key: string, value: any) => void;
}

/** A single row in the Teams tab represents one TEAM (not a member-in-team).
 *  Click the row to edit the team \u2014 that opens the TeamEditor showing the
 *  team's properties and a managed list of its members. The Members tab
 *  is the place to see members; this tab is purely for teams. */
type TeamRow = Team & {
  _memberCount: number;
  _leaderNames: string; // comma-joined for sort/search; rendered with badges
  _leaderIds: string[];
  _memberIds: string[];
  _parentName: string;
};

function TeamsTab({
  teams,
  members,
  teamAssignments,
  memberName,
  getTeamMemberAssignments: _getTeamMemberAssignments,
  getTeamLeader: _getTeamLeader,
  onEdit,
  onDelete: _onDelete,
  onNew,
  onCellEdit: _onCellEdit,
}: TeamsTabProps) {
  // One row per team, with derived member/leader columns. Sorting + grouping
  // + sticky header come for free from DataTable (per feedback_tables_match
  // _sharepoint).
  const teamRows = useMemo<TeamRow[]>(() => {
    return teams.map((t) => {
      const assigns = teamAssignments.filter((ta) => ta.teamId === t.id);
      const leaderIds = assigns.filter((a) => a.teamRole === 'leader').map((a) => a.teamMemberId);
      const memberIds = assigns.map((a) => a.teamMemberId);
      const leaderNames = leaderIds.map((id) => memberName(id)).filter(Boolean);
      const parent = teams.find((p) => p.id === t.parentTeamId);
      return {
        ...t,
        _memberCount: memberIds.length,
        _leaderIds: leaderIds,
        _leaderNames: leaderNames.join(', '),
        _memberIds: memberIds,
        _parentName: parent?.name || '',
      };
    });
  }, [teams, teamAssignments, memberName]);

  const columns: Column<TeamRow>[] = [
    {
      key: 'name',
      label: 'Team',
      width: 240,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: r.color || 'var(--tm)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 700 }}>{r.name}</span>
          {r.shortCode && (
            <span
              style={{
                fontSize: 10,
                padding: '1px 5px',
                borderRadius: 3,
                background: 'var(--sa)',
                color: 'var(--tm)',
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
              }}
            >
              {r.shortCode}
            </span>
          )}
        </span>
      ),
      getValue: (r) => r.name,
    },
    {
      key: 'leaderNames',
      label: 'Leader',
      width: 180,
      render: (r) =>
        r._leaderIds.length === 0 ? (
          <span style={{ color: 'var(--tm)', fontStyle: 'italic', fontSize: 13 }}>
            \u2014 none \u2014
          </span>
        ) : (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {r._leaderIds.map((id) => (
              <span
                key={id}
                style={{
                  fontSize: 11,
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: 'rgba(var(--acr),0.12)',
                  color: 'var(--ac)',
                  fontWeight: 700,
                  border: '1px solid rgba(var(--acr),0.25)',
                }}
                title={memberName(id)}
              >
                {'\u2605'} {memberName(id)}
              </span>
            ))}
          </span>
        ),
      getValue: (r) => r._leaderNames,
    },
    {
      key: 'memberCount',
      label: 'Members',
      width: 100,
      render: (r) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {r._memberCount}
        </span>
      ),
      getValue: (r) => r._memberCount,
      sortable: true,
    },
    {
      key: 'parentName',
      label: 'Parent Team',
      width: 160,
      render: (r) =>
        r._parentName ? (
          <span style={{ fontSize: 13 }}>{r._parentName}</span>
        ) : (
          <span style={{ color: 'var(--tm)', fontSize: 13 }}>top-level</span>
        ),
      getValue: (r) => r._parentName,
    },
    {
      key: 'description',
      label: 'Description',
      width: 280,
      render: (r) => (
        <span
          style={{
            color: r.description ? 'var(--tx)' : 'var(--tm)',
            fontSize: 13,
            fontStyle: r.description ? 'normal' : 'italic',
          }}
        >
          {r.description || '\u2014 none \u2014'}
        </span>
      ),
      getValue: (r) => r.description || '',
    },
  ];

  // Members not currently on any team \u2014 surfaced under the table so an
  // admin can see who hasn't been assigned anywhere at a glance.
  const unassigned = members.filter((m) => !teamAssignments.some((ta) => ta.teamMemberId === m.id));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={onNew} style={btnPrimary}>
          + New Team
        </button>
      </div>
      <DataTable<TeamRow>
        columns={columns}
        data={teamRows}
        keyField="id"
        storageKey="teams-tab-teams-list"
        initialSort={{ key: 'name', direction: 'asc' }}
        emptyTitle="No teams yet"
        emptyMessage="Create a team with the New Team button. Teams group people across departments and drive forecasting/scope filters."
        onRowClick={(r) => {
          // Look up the clean Team record. TeamRow carries synthetic
          // `_memberCount` / `_leaderIds` / etc. for sort+display; if those
          // ride into the editor and out to TeamService.saveTeam, the
          // server tries to insert them as columns and fails with
          // `column "_member_count" of relation "teams" does not exist`.
          const clean = teams.find((t) => t.id === r.id);
          if (clean) onEdit(clean);
        }}
      />
      {unassigned.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            background: 'var(--sa)',
            borderRadius: 8,
            border: '1px solid var(--bd)',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tm)', marginBottom: 6 }}>
            Members not on any team ({unassigned.length})
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {unassigned.map((m) => (
              <span
                key={m.id}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: 'var(--sf)',
                  border: '1px solid var(--bd)',
                  color: 'var(--tx)',
                }}
              >
                {m.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Project Picker (searchable, mirrors TeamMemberPicker pattern) ───

function ProjectPicker({
  availableProjects,
  onAssignProject,
}: {
  availableProjects: Project[];
  onAssignProject: (projectId: string) => void;
}) {
  const [q, setQ] = useState('');
  const filtered = q
    ? availableProjects.filter(
        (p) =>
          (p.projectNumber || '').toLowerCase().includes(q.toLowerCase()) ||
          p.name.toLowerCase().includes(q.toLowerCase())
      )
    : availableProjects;

  return (
    <div
      style={{
        marginBottom: 8,
        padding: 10,
        background: 'var(--sf)',
        borderRadius: 8,
        border: '1px solid var(--bd)',
      }}
    >
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search projects..."
        autoFocus
        style={{
          width: '100%',
          padding: '6px 10px',
          borderRadius: 5,
          border: '1px solid var(--bd)',
          background: 'var(--ib)',
          color: 'var(--tx)',
          fontSize: 13,
          marginBottom: 6,
        }}
      />
      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--tm)', fontStyle: 'italic', padding: '4px 0' }}>
          {availableProjects.length === 0 ? 'All projects already assigned.' : 'No matches.'}
        </div>
      ) : (
        <div
          style={{
            maxHeight: 180,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {filtered.map((p) => (
            <div
              key={p.id}
              onClick={() => onAssignProject(p.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 7px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13,
                transition: 'background .1s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {p.projectNumber && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: 'var(--ac)',
                    fontWeight: 700,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  {p.projectNumber}
                </span>
              )}
              <span style={{ flex: 1 }}>{p.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Member Editor Modal ───

interface MemberEditorProps {
  member: TeamMember;
  dirty: boolean;
  roles: Role[];
  billingRoles: BillingRole[];
  departments: Department[];
  /** Whether the current viewer can see / edit financial details
   *  (rate suffix on Title dropdown, hourly-rate override field).
   *  Driven by team_members.view_details. */
  canSeeRates: boolean;
  members: TeamMember[];
  projects: Project[];
  memberTeamAssignments: TeamAssignment[];
  memberProjectAssignments: ProjectAssignment[];
  availableProjects: Project[];
  addingProject: boolean;
  teamName: (id: string) => string;
  roleName: (m: TeamMember) => string;
  onUpdate: (updates: Partial<TeamMember>) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  onAssignProject: (projectId: string) => void;
  onUnassignProject: (assignmentId: string) => void;
  onToggleAddProject: () => void;
}

function MemberEditor({
  member,
  dirty,
  roles,
  billingRoles,
  departments,
  canSeeRates,
  members,
  projects,
  memberTeamAssignments,
  memberProjectAssignments,
  availableProjects,
  addingProject,
  teamName,
  roleName,
  onUpdate,
  onSave,
  onDelete,
  onClose,
  onAssignProject,
  onUnassignProject,
  onToggleAddProject,
}: MemberEditorProps) {
  function projectById(id: string): Project | undefined {
    return projects.find((p) => p.id === id);
  }

  return (
    <div
      style={modalOverlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={modalBox}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--bd)',
            flexShrink: 0,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {member.id ? 'Edit Team Member' : 'New Team Member'}
          </h3>
          <button onClick={onClose} style={closeBtn} title="Close" aria-label="Close">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Left — Details */}
            <div>
              <div style={sectionHeading}>Member Details</div>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Full Name</span>
                <input
                  value={member.name}
                  onChange={(e) => onUpdate({ name: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g. Jane Smith"
                  autoFocus
                />
              </label>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Email</span>
                <input
                  type="email"
                  value={member.email || ''}
                  onChange={(e) => onUpdate({ email: e.target.value })}
                  style={inputStyle}
                  placeholder="jane@example.com"
                />
              </label>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Privilege Role</span>
                <SearchableSelect
                  value={member.roleId || ''}
                  options={roles.map((r) => ({ value: r.id, label: r.name }))}
                  onChange={(v) => {
                    const roleId = v || undefined;
                    const rn = roleId ? roles.find((r) => r.id === roleId)?.name || '' : '';
                    onUpdate({ roleId, role: rn });
                  }}
                  placeholder="-- Select Role --"
                  searchPlaceholder="Search roles…"
                  clearable={false}
                />
              </label>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Title</span>
                <SearchableSelect
                  value={member.billingRoleId || ''}
                  options={billingRoles.map((br) => ({
                    value: br.id,
                    label: br.name,
                    // Rate hint is gated — display-only titles never show a rate.
                    hint:
                      canSeeRates && br.defaultRate != null ? `$${br.defaultRate}/hr` : undefined,
                  }))}
                  onChange={(v) => onUpdate({ billingRoleId: v || undefined })}
                  placeholder="-- None --"
                  searchPlaceholder="Search titles…"
                />
              </label>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Department</span>
                <SearchableSelect
                  value={member.departmentId || ''}
                  options={departments.map((d) => ({
                    value: d.id,
                    label: d.abbreviation ? `${d.abbreviation} — ${d.name}` : d.name,
                  }))}
                  onChange={(v) => onUpdate({ departmentId: v || undefined })}
                  placeholder="-- None --"
                  searchPlaceholder="Search departments…"
                />
              </label>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Employment Type</span>
                <div
                  style={{
                    display: 'flex',
                    gap: 0,
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: '1px solid var(--bd)',
                  }}
                >
                  {(
                    [
                      { value: 'salary', label: 'Salary', sub: '40h target' },
                      { value: 'full_time_hourly', label: 'Full-time Hourly', sub: '40h target' },
                      { value: 'part_time_hourly', label: 'Part-time', sub: '≤20h cap' },
                    ] as const
                  ).map((opt) => {
                    const active = (member.employmentType || 'salary') === opt.value;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => {
                          // Auto-set capacity to a sensible default unless the user
                          // already overrode it — keeps the indicator math sane.
                          const wantCap = opt.value === 'part_time_hourly' ? 20 : 40;
                          const next: Partial<TeamMember> = { employmentType: opt.value };
                          if (
                            member.weeklyCapacityHours == null ||
                            member.weeklyCapacityHours === 20 ||
                            member.weeklyCapacityHours === 40
                          ) {
                            next.weeklyCapacityHours = wantCap;
                          }
                          onUpdate(next);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 6px',
                          background: active ? 'var(--ac)' : 'var(--ib)',
                          color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                          border: 'none',
                          borderRight: '1px solid var(--bd)',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'background .12s, color .12s',
                          lineHeight: 1.2,
                        }}
                      >
                        <div>{opt.label}</div>
                        <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </label>

              <label style={fieldBlock}>
                <span style={fieldLabel}>Weekly Capacity (hours)</span>
                <input
                  type="number"
                  min="0"
                  max="80"
                  step="1"
                  value={member.weeklyCapacityHours ?? 40}
                  onChange={(e) => {
                    const n = parseFloat(e.target.value);
                    onUpdate({ weeklyCapacityHours: isFinite(n) ? n : undefined });
                  }}
                  style={{ ...inputStyle, width: 120, fontFamily: "'JetBrains Mono', monospace" }}
                />
                <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 4 }}>
                  Used by the forecast grid capacity bar — under-allocation goes red below this for
                  full-timers, over-allocation goes red above for part-timers.
                </div>
              </label>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <div
                    onClick={() => onUpdate({ active: !member.active })}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      flexShrink: 0,
                      border: `2px solid ${member.active ? 'var(--ac)' : 'var(--bd)'}`,
                      background: member.active ? 'var(--ac)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    {member.active && (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 13 }}>Active member</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onSave} style={btnPrimary}>
                  {dirty ? 'Save \u2022' : 'Save'}
                </button>
                {member.id && (
                  <button onClick={() => onDelete(member.id)} style={btnDanger}>
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Right — Teams & Projects */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Team Membership */}
              <div>
                <div style={sectionHeading}>Team Membership</div>
                {memberTeamAssignments.length === 0 ? (
                  <div style={emptyPill}>
                    {member.id
                      ? 'Not assigned to any teams.'
                      : 'Save member first, then assign to teams.'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {memberTeamAssignments.map((ta) => (
                      <div
                        key={ta.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '7px 10px',
                          background: 'var(--sf)',
                          borderRadius: 6,
                          border: '1px solid var(--bd)',
                        }}
                      >
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                          {teamName(ta.teamId)}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: 10,
                            textTransform: 'uppercase',
                            letterSpacing: '.04em',
                            background:
                              ta.teamRole === 'leader' ? 'rgba(var(--acr),0.15)' : 'var(--sa)',
                            color: ta.teamRole === 'leader' ? 'var(--ac)' : 'var(--tm)',
                            border:
                              ta.teamRole === 'leader'
                                ? '1px solid rgba(var(--acr),0.3)'
                                : '1px solid var(--bd)',
                          }}
                        >
                          {ta.teamRole}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Project Assignments */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <div style={sectionHeading}>Project Assignments</div>
                  {member.id && (
                    <button
                      onClick={onToggleAddProject}
                      style={{
                        padding: '3px 10px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: addingProject ? 'var(--sa)' : 'var(--ac)',
                        color: addingProject ? 'var(--tm)' : 'var(--color-text-inverse)',
                        border: addingProject ? '1px solid var(--bd)' : 'none',
                      }}
                    >
                      {addingProject ? 'Cancel' : '+ Assign'}
                    </button>
                  )}
                </div>

                {!member.id ? (
                  <div style={emptyPill}>Save member first to assign projects.</div>
                ) : (
                  <>
                    {/* Project picker */}
                    {addingProject && (
                      <ProjectPicker
                        availableProjects={availableProjects}
                        onAssignProject={onAssignProject}
                      />
                    )}

                    {/* Assigned list */}
                    {memberProjectAssignments.length === 0 ? (
                      <div style={emptyPill}>No projects assigned yet.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {memberProjectAssignments.map((a) => {
                          const p = projectById(a.projectId);
                          return (
                            <div
                              key={a.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '7px 10px',
                                background: 'var(--sf)',
                                borderRadius: 6,
                                border: '1px solid var(--bd)',
                              }}
                            >
                              {p?.projectNumber && (
                                <span
                                  style={{
                                    fontFamily: "'JetBrains Mono', monospace",
                                    color: 'var(--ac)',
                                    fontWeight: 700,
                                    fontSize: 11,
                                    flexShrink: 0,
                                    minWidth: 55,
                                  }}
                                >
                                  {p.projectNumber}
                                </span>
                              )}
                              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                                {p?.name || a.projectId}
                              </span>
                              <button
                                onClick={() => onUnassignProject(a.id)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'var(--dg)',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  borderRadius: 3,
                                  lineHeight: 1,
                                  flexShrink: 0,
                                }}
                                title="Remove"
                                aria-label="Remove"
                              >
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Team Editor Modal ───

interface TeamEditorProps {
  team: Team;
  dirty: boolean;
  teamAssignments: TeamAssignment[];
  availableMembers: TeamMember[];
  addingMember: boolean;
  members: TeamMember[];
  allTeams: Team[];
  memberName: (id?: string) => string;
  onUpdate: (updates: Partial<Team>) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  onAddMember: (memberId: string, role: 'leader' | 'member') => void;
  onRemoveMember: (assignmentId: string) => void;
  onChangeRole: (assignmentId: string, newRole: 'leader' | 'member') => void;
  onToggleAddMember: () => void;
}

function TeamMemberPicker({
  availableMembers,
  onAddMember,
}: {
  availableMembers: TeamMember[];
  onAddMember: (id: string, role: 'leader' | 'member') => void;
}) {
  const [q, setQ] = useState('');
  const filtered = q
    ? availableMembers.filter(
        (m) =>
          m.name.toLowerCase().includes(q.toLowerCase()) ||
          (m.email || '').toLowerCase().includes(q.toLowerCase())
      )
    : availableMembers;

  return (
    <div
      style={{
        marginBottom: 10,
        padding: 10,
        background: 'var(--sf)',
        borderRadius: 8,
        border: '1px solid var(--bd)',
      }}
    >
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search members..."
        autoFocus
        style={{
          width: '100%',
          padding: '6px 10px',
          borderRadius: 5,
          border: '1px solid var(--bd)',
          background: 'var(--ib)',
          color: 'var(--tx)',
          fontSize: 13,
          marginBottom: 6,
        }}
      />
      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--tm)', fontStyle: 'italic', padding: '4px 0' }}>
          {availableMembers.length === 0 ? 'All members are already in this team.' : 'No matches.'}
        </div>
      ) : (
        <div
          style={{
            maxHeight: 200,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {filtered.map((m) => (
            <div
              key={m.id}
              onClick={() => onAddMember(m.id, 'member')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {m.name}
              {m.email && (
                <span style={{ fontSize: 11, color: 'var(--tm)', marginLeft: 'auto' }}>
                  {m.email}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamEditor({
  team,
  dirty,
  teamAssignments,
  availableMembers,
  addingMember,
  members,
  allTeams,
  memberName,
  onUpdate,
  onSave,
  onDelete,
  onClose,
  onAddMember,
  onRemoveMember,
  onChangeRole,
  onToggleAddMember,
}: TeamEditorProps) {
  // Teams available as parent (exclude self and descendants to prevent cycles)
  const getDescendantIds = (id: string): Set<string> => {
    const desc = new Set<string>();
    const queue = allTeams.filter((t) => t.parentTeamId === id);
    while (queue.length) {
      const t = queue.pop()!;
      desc.add(t.id);
      queue.push(...allTeams.filter((c) => c.parentTeamId === t.id));
    }
    return desc;
  };
  const descendantIds = team.id ? getDescendantIds(team.id) : new Set<string>();
  const parentOptions = allTeams.filter((t) => t.id !== team.id && !descendantIds.has(t.id));
  return (
    <div
      style={modalOverlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{ ...modalBox, maxWidth: 520 }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--bd)',
            flexShrink: 0,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {team.id ? 'Edit Team' : 'New Team'}
          </h3>
          <button onClick={onClose} style={closeBtn} title="Close" aria-label="Close">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          <div style={sectionHeading}>Team Details</div>

          <label style={fieldBlock}>
            <span style={fieldLabel}>Team Name</span>
            <input
              value={team.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Mechanical Systems"
              autoFocus
            />
          </label>

          <label style={fieldBlock}>
            <span style={fieldLabel}>Description</span>
            <textarea
              value={team.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              style={{ ...inputStyle, height: 64, resize: 'vertical' }}
              placeholder="Optional description..."
            />
          </label>

          <label style={fieldBlock}>
            <span style={fieldLabel}>Parent Team</span>
            <SearchableSelect
              value={team.parentTeamId || ''}
              options={parentOptions.map((t) => ({ value: t.id, label: t.name }))}
              onChange={(v) => onUpdate({ parentTeamId: v || undefined })}
              placeholder="-- None (top-level) --"
              searchPlaceholder="Search teams…"
            />
          </label>

          <label style={fieldBlock}>
            <span style={fieldLabel}>Short Code (badge)</span>
            <input
              value={team.shortCode || ''}
              onChange={(e) => onUpdate({ shortCode: e.target.value })}
              style={{ ...inputStyle, width: 100 }}
              placeholder="e.g. M, E, P, T"
              maxLength={4}
            />
          </label>

          <div style={{ ...fieldBlock, marginBottom: 20 }}>
            <span style={fieldLabel}>Team Color</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {TEAM_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => onUpdate({ color: c })}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: team.color === c ? '3px solid var(--tx)' : '2px solid transparent',
                    background: c,
                    cursor: 'pointer',
                    outline: team.color === c ? '2px solid var(--bg)' : 'none',
                    outlineOffset: -3,
                    transition: 'all .1s',
                  }}
                  title={c}
                  aria-label={`Team color ${c}`}
                />
              ))}
              <input
                type="color"
                value={team.color || '#4A90D9'}
                onChange={(e) => onUpdate({ color: e.target.value })}
                style={{
                  width: 26,
                  height: 26,
                  border: '1px solid var(--bd)',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  padding: 0,
                }}
                title="Custom color"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <button onClick={onSave} style={btnPrimary}>
              {dirty ? 'Save \u2022' : 'Save'}
            </button>
            {team.id && (
              <button onClick={() => onDelete(team.id)} style={btnDanger}>
                Delete Team
              </button>
            )}
          </div>

          {/* Members section */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={sectionHeading}>Members</div>
            {team.id && (
              <button
                onClick={onToggleAddMember}
                style={{
                  padding: '3px 10px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: addingMember ? 'var(--sa)' : 'var(--ac)',
                  color: addingMember ? 'var(--tm)' : 'var(--color-text-inverse)',
                  border: addingMember ? '1px solid var(--bd)' : 'none',
                }}
              >
                {addingMember ? 'Cancel' : '+ Add Member'}
              </button>
            )}
          </div>

          {!team.id && <div style={emptyPill}>Save the team first to add members.</div>}

          {/* Add member picker (stays open, searchable, single-click add as member) */}
          {team.id && addingMember && (
            <TeamMemberPicker availableMembers={availableMembers} onAddMember={onAddMember} />
          )}

          {/* Member list */}
          {team.id && teamAssignments.length === 0 && !addingMember && (
            <div style={emptyPill}>No members assigned yet.</div>
          )}

          {team.id && teamAssignments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {teamAssignments.map((ta) => {
                const m = members.find((x) => x.id === ta.teamMemberId);
                return (
                  <div
                    key={ta.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 10px',
                      background: 'var(--sf)',
                      borderRadius: 6,
                      border: '1px solid var(--bd)',
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                      {m?.name || memberName(ta.teamMemberId)}
                    </span>
                    {/* Binary leader/member toggle. Not a list of data, so a
                        button-pair is the right primitive (per no-plain-dropdown
                        memory rule — that rule is for picking-from-data; this
                        is a hard enum). */}
                    <div
                      role="group"
                      aria-label={`${m?.name || 'Member'} team role`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'inline-flex',
                        borderRadius: 4,
                        border: '1px solid var(--bd)',
                        overflow: 'hidden',
                      }}
                    >
                      {(['member', 'leader'] as const).map((opt) => {
                        const active = ta.teamRole === opt;
                        return (
                          <button
                            key={opt}
                            onClick={() => onChangeRole(ta.id, opt)}
                            aria-pressed={active}
                            style={{
                              padding: '2px 8px',
                              border: 'none',
                              background: active
                                ? 'color-mix(in srgb, var(--ac) 14%, transparent)'
                                : 'var(--sa)',
                              color: active ? 'var(--ac)' : 'var(--tm)',
                              fontSize: 11,
                              fontWeight: active ? 700 : 600,
                              cursor: 'pointer',
                              textTransform: 'capitalize',
                            }}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => onRemoveMember(ta.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--dg)',
                        cursor: 'pointer',
                        padding: '2px 4px',
                        borderRadius: 3,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                      title="Remove from team"
                      aria-label="Remove from team"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───

// Crisp vector "+" for header action buttons (replaces the text "+ " glyph
// so the icon scales cleanly and inherits currentColor in light/dark).
function PlusIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// Layout add-on for buttons that carry a leading icon + label.
const btnWithIcon: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tx)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--dg)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
};

const iconBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tm)',
  cursor: 'pointer',
  padding: '4px 5px',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const closeBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tm)',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};

const fieldBlock: React.CSSProperties = {
  display: 'block',
  marginBottom: 12,
};

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--tm)',
  marginBottom: 4,
};

const sectionHeading: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  marginBottom: 10,
};

const emptyPill: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'center',
  background: 'var(--sf)',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  color: 'var(--tm)',
  fontSize: 13,
  fontStyle: 'italic',
};

const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(2px)',
};

const modalBox: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--bd)',
  borderRadius: 10,
  boxShadow: '0 8px 40px rgba(0,0,0,.22)',
  width: '90vw',
  maxWidth: 840,
  maxHeight: '88vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

(window as any).TeamPage = TeamPage;
export { TeamPage };
